import React, { useState, useEffect, useCallback, useMemo, useRef } from 'react';
import { createPortal } from 'react-dom';
import {
  ClipboardList,
  Home,
  Landmark,
  GraduationCap,
  FileEdit,
  Activity,
  RefreshCw,
  Search,
  Eye,
  EyeOff,
  CheckCircle2,
  XCircle,
  AlertTriangle,
  Clock,
  Calendar,
  User,
  Users,
  X,
  Image as ImageIcon,
  Video as VideoIcon,
  FileText,
  Sparkles,
  MessageSquare,
  Building2,
  Newspaper,
  PlusCircle,
  Trash2,
  Edit3,
  Link2,
  ExternalLink,
  AlignLeft,
  Type,
  Tag,
  List,
  Maximize2,
  Briefcase,
  Phone,
  PanelBottom,
  Quote,
  Lock
} from 'lucide-react';
import '../principal-admin/ApprovalDashboard.css';
import './ApprovalStatusHistory.css';
import { cleanGoogleMapsEmbedUrl } from '../../utils/mapUtils';

/**
 * Menu mapping helper to identify menu category, icon, and color badge
 */
function getMenuCategory(page, sectionType, fieldName = '', fieldId = '') {
  const p = (page || '').toLowerCase();
  const s = (sectionType || '').toLowerCase();
  const fn = (fieldName || '').toLowerCase();
  const fid = (fieldId || '').toLowerCase();

  if (p.includes('footer') || s.includes('footer')) {
    return { name: 'Footer', key: 'footer', color: '#475569', bg: '#f8fafc', border: '#cbd5e1', icon: PanelBottom };
  }

  const isNewsHeroSlide =
    (s === 'hero' || s === 'news-hero' || s === 'news-updates-hero') &&
    (fn.includes('news') || (p === 'homepage' && (fid.startsWith('slides.') || fn.includes('slide'))));

  if (
    p === 'news-updates' ||
    p === 'news-and-updates' ||
    (p === 'news' && s !== 'news') ||
    s.startsWith('news-') ||
    s.startsWith('news_') ||
    s.includes('article') ||
    s.includes('press') ||
    isNewsHeroSlide
  ) {
    return { name: 'News & Updates', key: 'news-updates', color: '#0284c7', bg: '#f0f9ff', border: '#bae6fd', icon: Newspaper };
  }
  if (p === 'aboutus' || p === 'about-us' || s.startsWith('about-us') || s.startsWith('aboutus')) {
    return { name: 'About Us', key: 'about-us', color: '#7c3aed', bg: '#f5f3ff', border: '#ddd6fe', icon: Landmark };
  }
  if (p === 'education-program' || p === 'education' || s.startsWith('education') || s.startsWith('edu-') || s.includes('secondary')) {
    return { name: 'Education Program', key: 'education-program', color: '#059669', bg: '#ecfdf5', border: '#a7f3d0', icon: GraduationCap };
  }
  if (p === 'admissions' || s.startsWith('admissions') || s.startsWith('adm-')) {
    return { name: 'Admissions', key: 'admissions', color: '#d97706', bg: '#fffbeb', border: '#fde68a', icon: FileEdit };
  }
  if (p === 'student-life' || p === 'studentlife' || s.startsWith('student-life') || s.startsWith('studentlife') || s.startsWith('life-') || s.includes('house')) {
    return { name: 'Student Life', key: 'student-life', color: '#db2777', bg: '#fdf2f8', border: '#fbcfe8', icon: Activity };
  }
  if (p === 'facilities' || p === 'facility' || s.startsWith('facilities') || s.startsWith('facility')) {
    return { name: 'Facilities', key: 'facilities', color: '#0284c7', bg: '#f0f9ff', border: '#bae6fd', icon: Building2 };
  }
  if (p === 'parents' || p === 'parent' || s.startsWith('parents') || s.startsWith('parent') || s.includes('palc') || s.includes('handbook')) {
    return { name: 'Parents', key: 'parents', color: '#8b5cf6', bg: '#f5f3ff', border: '#ddd6fe', icon: Users };
  }
  if (p === 'careers' || p === 'career' || s.startsWith('careers') || s.startsWith('career') || s.includes('hiring') || s.includes('position') || s.includes('internship')) {
    return { name: 'Careers', key: 'careers', color: '#4f46e5', bg: '#eef2ff', border: '#c7d2fe', icon: Briefcase };
  }
  if (
    p === 'contact-us' ||
    p === 'contact' ||
    s.startsWith('contact') ||
    s.includes('faq') ||
    s.includes('partnership') ||
    s.includes('testimonial') ||
    fid.startsWith('testimonials.')
  ) {
    return { name: 'Contact Us', key: 'contact-us', color: '#0d9488', bg: '#f0fdfa', border: '#99f6e4', icon: Phone };
  }
  return { name: 'Homepage', key: 'homepage', color: '#2563eb', bg: '#eff6ff', border: '#bfdbfe', icon: Home };
}

/**
 * Human-readable section name formatter
 */
function getSectionDisplayName(sectionType, fieldName = '', fieldId = '') {
  const s = (sectionType || '').toLowerCase();
  const fn = (fieldName || '').toLowerCase();
  const fid = (fieldId || '').toLowerCase();

  if (s.includes('footer')) return 'Footer Settings';

  // Admissions Specific Sections
  if (s === 'admissions-hero' || (s.includes('admissions') && (s.includes('hero') || fn.includes('hero')))) return 'Admissions Hero Section';
  if (s === 'admissions-levels' || (s.includes('admissions') && s.includes('level'))) return 'Admission Levels';
  if (s === 'admissions-requirements' || (s.includes('admissions') && s.includes('requirement'))) return 'Admissions Requirements';
  if (s === 'admissions-process' || (s.includes('admissions') && s.includes('process'))) return 'Admissions Application Process';
  if (s === 'admissions-fees' || s === 'admissions-fees-payment' || (s.includes('admissions') && s.includes('fee'))) return 'Fees & Payment Information';
  if (s === 'admissions-dates' || s === 'admissions-key-dates' || (s.includes('admissions') && (s.includes('date') || s.includes('timeline')))) return 'Admissions Timeline & Key Dates';
  if (s === 'admissions-online-application' || s === 'admissions-online-app' || (s.includes('admissions') && (s.includes('online') || s.includes('call-to-action') || s.includes('cta')))) return 'Online Application Call to Action';
  if (s.includes('admissions') && s.includes('scholarship')) return 'Scholarships & Aid';

  // Student Life Specific Sections
  if (s.includes('house-detail') || s.includes('house_detail')) return 'House System Detail Pages';
  if (s === 'student-life-hero' || s === 'studentlife-hero' || ((s.includes('student-life') || s.includes('studentlife')) && s.includes('hero'))) return 'Student Life Hero Section';
  if (s === 'student-life-clubs' || s === 'studentlife-clubs' || ((s.includes('student-life') || s.includes('studentlife')) && s.includes('club'))) return 'Clubs & Activities';
  if (s === 'student-life-events' || s === 'studentlife-events' || ((s.includes('student-life') || s.includes('studentlife')) && (s.includes('event') || s.includes('competition')))) return 'Events & Competitions';
  if (s === 'student-life-house-system' || s === 'studentlife-house-system' || ((s.includes('student-life') || s.includes('studentlife')) && (s.includes('house') || s.includes('team')))) return 'House System & Teams';
  if (s === 'student-life-wellbeing' || s === 'studentlife-wellbeing' || ((s.includes('student-life') || s.includes('studentlife')) && (s.includes('wellbeing') || s.includes('sel')))) return 'Wellbeing & Social Emotional Learning';
  if (s === 'student-life-character-shaping' || s === 'studentlife-character-shaping' || ((s.includes('student-life') || s.includes('studentlife')) && (s.includes('character') || s.includes('shaping')))) return 'Character Shaping Program';
  if (s === 'student-life-leaps' || s === 'studentlife-leaps' || ((s.includes('student-life') || s.includes('studentlife')) && s.includes('leap'))) return 'LEAPS Framework';

  // Facilities Specific Sections
  if (s.includes('overview-detail') || s.includes('overview_detail')) return 'Campus Overview Detail Pages';
  if (s.includes('tech-detail') || s.includes('technology-detail') || s.includes('tech_detail')) return 'Technology & Innovation Detail Pages';
  if (s === 'facilities-hero' || s === 'facilities-overview-hero' || ((s.includes('facilities') || s.includes('facility')) && s.includes('hero'))) return 'Facilities Hero Section';

  // Parents Specific Sections
  if (s === 'parents-hero' || ((s.includes('parents') || s.includes('parent')) && s.includes('hero'))) return 'Parents Hero Section';
  if (s === 'parents-palc' || s === 'parents-learning-community' || s === 'palc') return 'Learning Community (PaLC)';
  if (s === 'parents-handbook' || s === 'parent-student-handbook' || s === 'handbook') return 'Parent & Student Handbook';
  if (s === 'parents-portal' || s === 'parent-portal-access' || s === 'portal') return 'Parent Portal Access';
  if (s === 'parents-communication' || s === 'communication-channels' || s === 'communication') return 'Communication Channels';

  // News Specific Sections
  if (s === 'news-hero' || s === 'news-updates-hero' || ((s.includes('news') || s.includes('updates')) && s.includes('hero'))) return 'News & Updates Hero Section';
  if (s === 'news-articles' || s === 'articles' || s === 'news-blog-articles' || (s.includes('news') && s.includes('article'))) return 'News & Blog Articles';
  if (s === 'news-press' || s === 'press' || s === 'news-press-releases' || (s.includes('news') && s.includes('press'))) return 'Press Releases';

  // Careers Specific Sections
  if (s === 'careers-hero' || ((s.includes('careers') || s.includes('career')) && s.includes('hero'))) return 'Careers Hero Section';
  if (s === 'careers-philosophy' || s === 'hiring-philosophy' || (s.includes('career') && s.includes('philosophy'))) return 'Hiring Philosophy & Culture';
  if (s === 'careers-positions' || s === 'open-positions' || (s.includes('career') && s.includes('position'))) return 'Open Positions & Vacancies';
  if (s === 'careers-opportunities' || s === 'internships-volunteering' || (s.includes('career') && (s.includes('intern') || s.includes('opportunit') || s.includes('volunteer')))) return 'Internships & Volunteering';

  // Contact Us Specific Sections
  if (s === 'contact-hero' || ((s.includes('contact') || s.includes('kontak')) && s.includes('hero'))) return 'Contact Us Hero Section';
  if (s === 'contact-info' || s === 'contact-location' || (s.includes('contact') && (s.includes('info') || s.includes('location')))) return 'Contact Info & Campus Location';
  if (s === 'contact-faq' || s === 'faq' || (s.includes('contact') && s.includes('faq'))) return 'Frequently Asked Questions (FAQ)';
  if (s === 'contact-partnerships' || s === 'partnerships' || (s.includes('contact') && s.includes('partner'))) return 'Partnerships & Affiliations';
  if (s === 'contact-testimonials' || fid.startsWith('testimonials.') || (s.includes('contact') && s.includes('testimoni')) || s.includes('testimonial')) return 'Testimonials Section';
  if (s === 'contact-countdown' || (s.includes('contact') && (s.includes('countdown') || s.includes('construction')))) return 'Campus Construction & Countdown';

  // Education Detail
  if (s.includes('secondary-detail') || s === 'secondary-page' || s === 'secondary') return 'Secondary Detail Page';

  if (s.includes('hero') || ((s === 'hero' || s === '') && (fn.includes('hero') || fid.startsWith('slides.')))) return 'Hero Section';
  if (s.includes('quick_links') || s.includes('quicklinks')) return 'Quick Links';
  if (s.includes('highlight')) return 'Highlights';
  if (s.includes('news')) return 'News & Updates';
  if (s.includes('countdown')) return 'Countdown';
  if (s.includes('leadership')) return 'School Leadership';
  if (s.includes('principal')) return 'Principal Message';
  if (s.includes('values')) return 'Vision, Mission & Values';
  if (s.includes('philosophy')) return 'School Philosophy';
  if (s.includes('history')) return 'School History';
  if (s.includes('curriculum')) return 'Curriculum Overview';
  if (s.includes('subject')) return 'Subjects Offered';
  if (s === 'education-program-levels-shell' || s === 'education-program-levels' || (s.includes('education') && s.includes('level'))) return 'Academic Pathways';
  if (s.includes('levels') || s.includes('pathway')) return 'Academic Pathways';
  if (s.includes('program')) return 'Program Levels';
  if (s.includes('assessment')) return 'Assessment System';
  if (s.includes('requirement')) return 'Admission Requirements';
  if (s.includes('date') || s.includes('timeline')) return 'Key Dates';
  if (s.includes('fee') || s.includes('payment')) return 'Fees & Payment';
  if (s.includes('process') || s.includes('step')) return 'Admission Process';
  if (s.includes('club')) return 'Clubs & Activities';
  if (s.includes('event')) return 'Events & Competitions';
  if (s.includes('house')) return 'House System';
  if (s.includes('wellbeing')) return 'Wellbeing & SEL';
  if (s.includes('character')) return 'Character Shaping';
  if (s.includes('leap')) return 'LEAPS Framework';
  if (s.includes('facilities-overview') || s.includes('campus-overview')) return 'Campus Facility Overview';
  if (s.includes('facilities-tech') || s.includes('technology')) return 'Technology & Innovation';
  if (s.includes('facilities-safety') || s.includes('safety')) return 'Safety & Security';
  if (s.includes('facilities-gallery') || s.includes('gallery')) return 'Gallery Photographs';
  if (s.includes('facilities-tour') || s.includes('virtual-tour')) return 'Virtual Campus Tour';

  return (sectionType || 'Section')
    .replace(/[-_]/g, ' ')
    .replace(/\b\w/g, c => c.toUpperCase());
}

/**
 * Determine the CMS form tab ID so Website Admin can jump directly to edit the section
 */
function getFormTabId(page = '', sectionType = '', fieldId = '', fieldName = '') {
  const p = (page || '').toLowerCase();
  const s = (sectionType || '').toLowerCase();
  const fid = (fieldId || '').toLowerCase();
  const fn = (fieldName || '').toLowerCase();

  // Testimonial publish/unpublish review queue
  if (fid.startsWith('testimonials.') || fn.includes('unpublish') || fn.includes('publish (on)')) {
    return 'testimonials-management';
  }

  // Footer
  if (s.includes('footer') || p.includes('footer')) return 'cms-footer';

  // Facilities
  if (s.includes('tour')) return 'cms-fac-tour';
  if (s.includes('gallery')) return 'cms-fac-gallery';
  if (s.includes('safety') || s.includes('security')) return 'cms-fac-safety';
  if (s.includes('tech-detail') || s.includes('technology-detail')) return 'cms-fac-tech-details';
  if (s.includes('tech')) return 'cms-fac-tech';
  if (s.includes('overview-detail')) return 'cms-fac-overview-details';
  if (s.includes('overview')) return 'cms-fac-overview';
  if ((p.includes('facilit') || s.includes('facilit')) && s.includes('hero')) return 'cms-fac-hero';

  // Careers
  if (s.includes('opportunit') || s.includes('intern') || s.includes('volunteer')) return 'cms-car-opportunities';
  if (s.includes('position')) return 'cms-car-positions';
  if (s.includes('philosophy') && (p.includes('career') || s.includes('career'))) return 'cms-car-philosophy';
  if ((p.includes('career') || s.includes('career')) && s.includes('hero')) return 'cms-car-hero';

  // Contact Us
  if (s.includes('countdown') && (p.includes('contact') || s.includes('contact'))) return 'cms-con-countdown';
  if (s.includes('testimonial')) return 'cms-con-testimonials';
  if (s.includes('partner')) return 'cms-con-partnerships';
  if (s.includes('faq')) return 'cms-con-faq';
  if (s.includes('contact-info') || s.includes('location')) return 'cms-con-info';
  if ((p.includes('contact') || s.includes('contact')) && s.includes('hero')) return 'cms-con-hero';

  // Parents
  if (s.includes('communication') || s.includes('channel')) return 'cms-par-communication';
  if (s.includes('portal')) return 'cms-par-portal';
  if (s.includes('handbook')) return 'cms-par-handbook';
  if (s.includes('palc') || s.includes('learning-community')) return 'cms-par-palc';
  if ((p.includes('parent') || s.includes('parent')) && s.includes('hero')) return 'cms-par-hero';

  // News & Updates
  if (s.includes('article') || s.includes('blog')) return 'cms-news-articles';
  if (s.includes('press')) return 'cms-news-press';
  if ((p.includes('news') || s.startsWith('news-')) && s.includes('hero')) return 'cms-news-hero';

  // Student Life
  if (s.includes('house-detail') || s.includes('house_detail')) return 'cms-sl-house-details';
  if (s.includes('house')) return 'cms-sl-houses';
  if (s.includes('leap')) return 'cms-sl-leaps';
  if (s.includes('character')) return 'cms-sl-character';
  if (s.includes('wellbeing')) return 'cms-sl-wellbeing';
  if (s.includes('event')) return 'cms-sl-events';
  if (s.includes('club')) return 'cms-sl-clubs';
  if ((p.includes('student') || s.includes('student')) && s.includes('hero')) return 'cms-sl-hero';

  // Admissions
  if (s.includes('online') || s.includes('cta')) return 'cms-adm-online-app';
  if (s.includes('date') || s.includes('timeline')) return 'cms-adm-timeline';
  if (s.includes('fee')) return 'cms-adm-fees';
  if (s.includes('process')) return 'cms-adm-process';
  if (s.includes('requirement')) return 'cms-adm-requirements';
  if ((p.includes('admission') || s.includes('admission')) && s.includes('level')) return 'cms-adm-levels';
  if ((p.includes('admission') || s.includes('admission')) && s.includes('hero')) return 'cms-adm-hero';

  // Education Program
  if (s.includes('secondary')) return 'cms-edu-secondary';
  if (s.includes('special-program')) return 'cms-edu-special-programs';
  if (s.includes('assessment')) return 'cms-edu-assessment';
  if (s.includes('learning') || s.includes('holistic')) return 'cms-edu-learning';
  if (s.includes('subject')) return 'cms-edu-subjects';
  if (s.includes('curriculum')) return 'cms-edu-curriculum';
  if (s.includes('pathway') || (p.includes('edu') && s.includes('level')) || s.includes('edu-program')) return 'cms-edu-programs';
  if ((p.includes('edu') || s.includes('edu')) && s.includes('hero')) return 'cms-education-hero';

  // About Us
  if (p.includes('about') || s.startsWith('about')) {
    if (s.includes('history')) return 'cms-about-history';
    if (s.includes('philosophy')) return 'cms-about-philosophy';
    if (s.includes('value')) return 'cms-about-values';
    if (s.includes('principal')) return 'cms-about-principal';
    if (s.includes('leadership')) return 'cms-about-leadership';
    if (s.includes('hero')) return 'cms-about-hero';
  }

  // Homepage
  if (s.includes('highlight')) return 'cms-highlights';
  if (s.includes('countdown')) return 'cms-countdown';
  if (s.includes('quick_links') || s.includes('quicklinks')) return 'cms-quicklinks';
  if (s === 'news') return 'cms-news';
  if (s.includes('hero')) return 'cms-hero';

  return null;
}

/**
 * Operation detector: Unpublish, Publish, Add New Item, Update Content, or Delete Item
 */
function getOperationInfo(fieldId, oldValue, newValue, fieldName) {
  const fid = (fieldId || '').toLowerCase();
  const fname = (fieldName || '').toLowerCase();

  let parsedNew = newValue;
  if (typeof newValue === 'string' && newValue.trim().startsWith('{')) {
    try {
      parsedNew = JSON.parse(newValue);
    } catch (_) {}
  }

  if (fid.endsWith('.unpublish') || fname.includes('unpublish') || parsedNew?.action === 'unpublish') {
    return {
      type: 'unpublish',
      label: 'Unpublish Item',
      shortLabel: 'UNPUBLISH',
      actionTitle: 'Unpublish / Deactivate (OFF)',
      color: '#dc2626',
      bg: '#fef2f2',
      border: '#fecaca',
      iconText: 'Unpublish (OFF)',
      icon: EyeOff
    };
  }

  if (fid.endsWith('.publish') || fname.startsWith('publish') || parsedNew?.action === 'publish') {
    return {
      type: 'publish',
      label: 'Publish Item',
      shortLabel: 'PUBLISH',
      actionTitle: 'Publish / Activate (ON)',
      color: '#16a34a',
      bg: '#f0fdf4',
      border: '#bbf7d0',
      iconText: 'Publish (ON)',
      icon: CheckCircle2
    };
  }

  const isExplicitUpdate =
    fid.endsWith('.update') ||
    fid.includes('.update') ||
    fid.includes('.edit') ||
    fname.startsWith('update') ||
    fname.startsWith('ubah') ||
    fname.startsWith('edit');
  const isExplicitDelete =
    fid.endsWith('.delete') ||
    fid.includes('.delete') ||
    fname.startsWith('delete') ||
    fname.startsWith('hapus') ||
    parsedNew?.action === 'delete';
  const isExplicitAdd =
    fid.endsWith('.add') ||
    fid.includes('.add') ||
    fname.startsWith('add') ||
    fname.startsWith('tambah');

  if (isExplicitUpdate) {
    return {
      type: 'update',
      label: 'Update Content',
      shortLabel: 'UPDATE',
      actionTitle: 'Update Item / Content',
      color: '#2563eb',
      bg: '#eff6ff',
      border: '#bfdbfe',
      iconText: 'Update Item',
      icon: Edit3
    };
  }

  if (isExplicitAdd) {
    return {
      type: 'add',
      label: 'Add New Item',
      shortLabel: 'ADD',
      actionTitle: 'Add New Item',
      color: '#16a34a',
      bg: '#f0fdf4',
      border: '#bbf7d0',
      iconText: 'Add Item',
      icon: PlusCircle
    };
  }

  if (isExplicitDelete) {
    return {
      type: 'delete',
      label: 'Delete Item',
      shortLabel: 'DELETE',
      actionTitle: 'Delete Item',
      color: '#dc2626',
      bg: '#fef2f2',
      border: '#fecaca',
      iconText: 'Delete Item',
      icon: Trash2
    };
  }

  const isCollectionItem = /^(slides|items|cards|programs|milestones|faqs|partners|testimonials)\.\d+$/i.test(fieldId || '');

  const isOldValueEmpty =
    oldValue === null ||
    oldValue === undefined ||
    oldValue === '' ||
    oldValue === '{}' ||
    oldValue === 'null' ||
    (typeof oldValue === 'object' && Object.keys(oldValue || {}).length === 0);
  const isNewValueEmpty =
    newValue === null ||
    newValue === undefined ||
    newValue === '' ||
    newValue === '{}' ||
    newValue === 'null' ||
    (typeof newValue === 'object' && Object.keys(newValue || {}).length === 0);

  if (isCollectionItem && !isOldValueEmpty && isNewValueEmpty) {
    return {
      type: 'delete',
      label: 'Delete Item',
      shortLabel: 'DELETE',
      actionTitle: 'Delete Item',
      color: '#dc2626',
      bg: '#fef2f2',
      border: '#fecaca',
      iconText: 'Delete Item',
      icon: Trash2
    };
  }

  if (isOldValueEmpty && !isNewValueEmpty) {
    return {
      type: 'add',
      label: 'Add New Item',
      shortLabel: 'ADD',
      actionTitle: 'Add New Item',
      color: '#16a34a',
      bg: '#f0fdf4',
      border: '#bbf7d0',
      iconText: 'Add Item',
      icon: PlusCircle
    };
  }

  return {
    type: 'update',
    label: 'Update Content',
    shortLabel: 'UPDATE',
    actionTitle: 'Update Item / Content',
    color: '#2563eb',
    bg: '#eff6ff',
    border: '#bfdbfe',
    iconText: 'Update Item',
    icon: Edit3
  };
}

/**
 * Clean field name formatter
 */
function formatFieldDisplayName(fieldName, fieldId) {
  if (fieldId === 'zones.easternCulture' || fieldId === 'zones.easternCulture.update') return 'Eastern Culture Value (Roots)';
  if (fieldId === 'zones.sel' || fieldId === 'zones.sel.update') return 'Social Emotional Learning (Trunk)';
  if (fieldId === 'zones.wellbeing' || fieldId === 'zones.wellbeing.update') return 'Wellbeing (Leaves)';
  if (fieldId === 'programs') return 'LEAPS Dimensions';
  if (fieldName && fieldName.trim() !== '') {
    return fieldName.replace(/^(Add|Delete|Tambah|Hapus)\s+([^:]+):\s*/i, '$2: ');
  }
  if (!fieldId) return 'Main Content';

  return fieldId
    .replace(/\.(add|update|delete|publish|unpublish)$/, '')
    .replace(/[._]/g, ' ')
    .replace(/\b\w/g, c => c.toUpperCase());
}

/**
 * Safe and resilient image preview component for approval diffs
 */
function SafeImagePreview({
  src,
  rawMediaRef = '',
  alt = 'Preview Photo',
  isBefore = false,
  isImgDifferent = false,
  onOpenLightbox = null,
  containerStyle = {},
  imageStyle = {}
}) {
  const [loadState, setLoadState] = useState('loading');
  const [activeSrc, setActiveSrc] = useState(src || '');
  const [retryAttempt, setRetryAttempt] = useState(0);

  useEffect(() => {
    setActiveSrc(src || '');
    setLoadState(src ? 'loading' : 'error');
    setRetryAttempt(0);
  }, [src]);

  const handleImageError = () => {
    if (retryAttempt === 0 && activeSrc) {
      setRetryAttempt(1);
      const sep = activeSrc.includes('?') ? '&' : '?';
      setActiveSrc(`${activeSrc}${sep}_t=${Date.now()}`);
      return;
    }
    setLoadState('error');
  };

  const handleManualRetry = e => {
    e.stopPropagation();
    setLoadState('loading');
    const sep = (src || '').includes('?') ? '&' : '?';
    setActiveSrc(`${src || activeSrc}${sep}_retry=${Date.now()}`);
  };

  const borderColor = isImgDifferent ? (isBefore ? '#ef4444' : '#10b981') : '#cbd5e1';
  const bgColor = isImgDifferent ? (isBefore ? '#fef2f2' : '#f0fdf4') : '#ffffff';
  const headerTitle = isBefore ? 'Current Published Photo' : (isImgDifferent ? 'Proposed New Photo' : 'Slide Photo');
  const badgeText = isImgDifferent ? (isBefore ? 'ORIGINAL' : 'NEW PHOTO') : '';

  return (
    <div
      className={`preview-image-container mb-3 ${isImgDifferent ? (isBefore ? 'field-card-diff-before' : 'field-card-diff-after') : ''}`}
      style={{
        width: '100%',
        boxSizing: 'border-box',
        borderRadius: '10px',
        overflow: 'hidden',
        border: `2px solid ${borderColor}`,
        padding: '10px',
        backgroundColor: bgColor,
        transition: 'all 0.2s ease',
        ...containerStyle
      }}
    >
      <div style={{ width: '100%', display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '2px 4px 8px' }}>
        <span
          style={{
            fontSize: '11px',
            fontWeight: '700',
            color: isImgDifferent ? (isBefore ? '#b91c1c' : '#15803d') : '#475569',
            textTransform: 'uppercase',
            letterSpacing: '0.04em'
          }}
        >
          {headerTitle}
        </span>
        {badgeText && (
          <span
            style={{
              fontSize: '10px',
              padding: '2px 8px',
              borderRadius: '999px',
              backgroundColor: isBefore ? '#fee2e2' : '#dcfce7',
              color: isBefore ? '#b91c1c' : '#15803d',
              fontWeight: '800',
              letterSpacing: '0.04em'
            }}
          >
            {badgeText}
          </span>
        )}
      </div>

      <div
        style={{
          position: 'relative',
          width: '100%',
          height: '190px',
          borderRadius: '6px',
          overflow: 'hidden',
          backgroundColor: '#0f172a08',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center'
        }}
      >
        {loadState === 'loading' && (
          <div
            style={{
              position: 'absolute',
              inset: 0,
              display: 'flex',
              flexDirection: 'column',
              alignItems: 'center',
              justifyContent: 'center',
              backgroundColor: isBefore ? '#fff5f5' : '#f0fdf4',
              zIndex: 2,
              gap: '8px',
              color: '#64748b',
              fontSize: '12px',
              fontWeight: '600'
            }}
          >
            <RefreshCw size={22} className="spinning" style={{ color: isImgDifferent ? (isBefore ? '#ef4444' : '#10b981') : '#3b82f6' }} />
            <span>Loading photo preview...</span>
          </div>
        )}

        {loadState === 'error' && (
          <div
            style={{
              position: 'absolute',
              inset: 0,
              display: 'flex',
              flexDirection: 'column',
              alignItems: 'center',
              justifyContent: 'center',
              padding: '16px',
              textAlign: 'center',
              backgroundColor: '#ffffff',
              zIndex: 3,
              gap: '8px'
            }}
          >
            <AlertTriangle size={24} style={{ color: '#d97706' }} />
            <div style={{ fontSize: '12.5px', fontWeight: '700', color: '#1e293b' }}>
              Photo preview unavailable
            </div>
            <div style={{ fontSize: '11px', color: '#64748b', maxWidth: '320px', wordBreak: 'break-all' }}>
              {rawMediaRef || activeSrc || '(No photo path)'}
            </div>
            <div style={{ display: 'flex', gap: '8px', marginTop: '4px' }}>
              <button
                type="button"
                onClick={handleManualRetry}
                style={{
                  display: 'inline-flex',
                  alignItems: 'center',
                  gap: '4px',
                  padding: '5px 10px',
                  fontSize: '11.5px',
                  fontWeight: '600',
                  color: '#1e293b',
                  background: '#f1f5f9',
                  border: '1px solid #cbd5e1',
                  borderRadius: '5px',
                  cursor: 'pointer'
                }}
              >
                <RefreshCw size={12} />
                <span>Retry</span>
              </button>
              {activeSrc && (
                <a
                  href={activeSrc}
                  target="_blank"
                  rel="noopener noreferrer"
                  style={{
                    display: 'inline-flex',
                    alignItems: 'center',
                    gap: '4px',
                    padding: '5px 10px',
                    fontSize: '11.5px',
                    fontWeight: '600',
                    color: '#2563eb',
                    background: '#eff6ff',
                    border: '1px solid #bfdbfe',
                    borderRadius: '5px',
                    textDecoration: 'none'
                  }}
                >
                  <ExternalLink size={12} />
                  <span>Open New Tab</span>
                </a>
              )}
            </div>
          </div>
        )}

        {activeSrc && (
          <img
            src={activeSrc}
            alt={alt}
            style={{
              width: '100%',
              height: '100%',
              objectFit: 'cover',
              borderRadius: '6px',
              display: loadState === 'error' ? 'none' : 'block',
              opacity: loadState === 'loaded' ? 1 : 0.4,
              transition: 'opacity 0.25s ease',
              ...imageStyle
            }}
            onLoad={() => setLoadState('loaded')}
            onError={handleImageError}
          />
        )}

        {loadState === 'loaded' && onOpenLightbox && (
          <button
            type="button"
            onClick={() => onOpenLightbox(activeSrc, headerTitle)}
            title="View full resolution photo"
            style={{
              position: 'absolute',
              bottom: '8px',
              right: '8px',
              display: 'inline-flex',
              alignItems: 'center',
              gap: '4px',
              padding: '4px 10px',
              fontSize: '11px',
              fontWeight: '700',
              color: '#ffffff',
              backgroundColor: 'rgba(15, 23, 42, 0.75)',
              border: 'none',
              borderRadius: '6px',
              cursor: 'pointer',
              backdropFilter: 'blur(4px)',
              transition: 'background-color 0.2s ease',
              zIndex: 4
            }}
            onMouseEnter={e => (e.currentTarget.style.backgroundColor = 'rgba(15, 23, 42, 0.95)')}
            onMouseLeave={e => (e.currentTarget.style.backgroundColor = 'rgba(15, 23, 42, 0.75)')}
          >
            <Maximize2 size={12} />
            <span>Full View</span>
          </button>
        )}
      </div>

      <div className="preview-media-caption" style={{ width: '100%', boxSizing: 'border-box', marginTop: '8px', display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: '5px', overflow: 'hidden' }}>
          <ImageIcon size={14} style={{ flexShrink: 0, color: '#64748b' }} />
          <span style={{ fontSize: '11.5px', color: '#475569', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
            Media File: <strong>{String(rawMediaRef || activeSrc)}</strong>
          </span>
        </div>
        {loadState === 'loaded' && activeSrc && (
          <a
            href={activeSrc}
            target="_blank"
            rel="noopener noreferrer"
            title="Open image in new tab"
            style={{ color: '#2563eb', display: 'inline-flex', alignItems: 'center', gap: '2px', fontSize: '11px', textDecoration: 'none', flexShrink: 0, marginLeft: '6px' }}
          >
            <ExternalLink size={11} />
            <span>New Tab</span>
          </a>
        )}
      </div>
    </div>
  );
}

/**
 * ApprovalStatusHistory Component (Website Admin Read-Only Submissions View)
 * Identical design, structure, filtering, and side-by-side comparison modal as Principal's Submissions page,
 * strictly read-only without Approve or Reject authority.
 */
function ApprovalStatusHistory({
  apiBase = 'https://permataharapansecondary.sch.id/api',
  onNavigateTab,
  onRefreshStats,
  viewerRole = 'website_admin'
}) {
  const [allSubmissions, setAllSubmissions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [selectedItem, setSelectedItem] = useState(null);
  const [showModal, setShowModal] = useState(false);
  const [detailLoading, setDetailLoading] = useState(false);
  const [selectedStatusFilter, setSelectedStatusFilter] = useState('pending');
  const [selectedMenuFilter, setSelectedMenuFilter] = useState('all');
  const [searchQuery, setSearchQuery] = useState('');
  const [mediaMap, setMediaMap] = useState({});
  const [testimonialsMap, setTestimonialsMap] = useState({ byId: {}, byAuthor: {} });
  const [statusCounts, setStatusCounts] = useState({ pending: 0, approved: 0, rejected: 0 });
  const [lightboxImage, setLightboxImage] = useState(null);
  const onRefreshStatsRef = useRef(onRefreshStats);

  useEffect(() => {
    onRefreshStatsRef.current = onRefreshStats;
  }, [onRefreshStats]);

  const parseJsonValue = useCallback(val => {
    if (val === null || val === undefined) return null;
    if (typeof val === 'object') return val;
    if (typeof val === 'string') {
      const trimmed = val.trim();
      if ((trimmed.startsWith('{') && trimmed.endsWith('}')) || (trimmed.startsWith('[') && trimmed.endsWith(']'))) {
        try {
          return JSON.parse(trimmed);
        } catch {
          return val;
        }
      }
    }
    return val;
  }, []);

  // Load all testimonials so quote text is always resolved even for status-toggle submissions
  const loadTestimonialsMap = useCallback(async () => {
    try {
      const res = await fetch(`${apiBase}/testimonials?limit=200&_t=${Date.now()}`, {
        credentials: 'include'
      });
      if (!res.ok) return;
      const json = await res.json();
      const list = json?.data?.testimonials || (Array.isArray(json?.data) ? json.data : []);
      const byId = {};
      const byAuthor = {};
      list.forEach(t => {
        if (t.id !== undefined && t.id !== null) {
          byId[String(t.id)] = t;
        }
        if (t.author_name) {
          const norm = String(t.author_name).trim().toLowerCase();
          if (!byAuthor[norm] || t.status === 'approved') {
            byAuthor[norm] = t;
          }
        }
      });
      setTestimonialsMap({ byId, byAuthor });
    } catch (err) {
      console.warn('[ApprovalStatusHistory] Testimonials lookup warning:', err);
    }
  }, [apiBase]);

  // Fetch media maps across modules to reliably resolve image IDs
  const loadMediaMap = useCallback(async () => {
    try {
      const savedToken = localStorage.getItem('ph_auth_token') || localStorage.getItem('auth_token') || sessionStorage.getItem('token');
      const fetchHeaders = {};
      if (savedToken) {
        fetchHeaders['Authorization'] = `Bearer ${savedToken}`;
      }

      const endpoints = [
        `${apiBase}/homepage/cms/media?type=image&_t=${Date.now()}`,
        `${apiBase}/homepage/cms/media?_t=${Date.now()}`,
        `${apiBase}/aboutus/cms/media?type=image&_t=${Date.now()}`,
        `${apiBase}/aboutus/cms/media?_t=${Date.now()}`,
        `${apiBase}/education/cms/media?type=image&_t=${Date.now()}`,
        `${apiBase}/admissions/cms/media?type=image&_t=${Date.now()}`,
        `${apiBase}/admissions/cms/media?_t=${Date.now()}`,
        `${apiBase}/studentlife/cms/media?type=image&_t=${Date.now()}`
      ];

      const responses = await Promise.allSettled(
        endpoints.map(url =>
          fetch(url, { credentials: 'include', headers: fetchHeaders }).then(res => (res.ok ? res.json() : null))
        )
      );

      const origin = (apiBase || '').replace(/\/api\/?$/, '');
      const map = {};
      responses.forEach(result => {
        if (result.status === 'fulfilled' && result.value) {
          const data = result.value.data || result.value;
          const items = Array.isArray(data) ? data : (data.items || []);
          items.forEach(m => {
            const id = m.id || m.media_id;
            const rawUrl = m.url || m.file_path;
            const filename = m.filename || m.file_name;
            if (rawUrl) {
              const fullUrl = rawUrl.startsWith('http') ? rawUrl : (origin ? `${origin}${rawUrl}` : rawUrl);
              if (id) {
                map[String(id)] = fullUrl;
                map[Number(id)] = fullUrl;
              }
              if (filename) {
                map[filename] = fullUrl;
              }
            }
          });
        }
      });

      setMediaMap(prev => ({ ...prev, ...map }));
    } catch (err) {
      console.warn('[ApprovalStatusHistory] Media map fetch warning:', err);
    }
  }, [apiBase]);

  const normalizeSubmissionItem = useCallback(it => ({
    id: Number(it.id),
    section_id: it.section_id !== undefined && it.section_id !== null ? Number(it.section_id) : null,
    section_type: it.section_type || it.section_name || 'general',
    page: it.page || it.change_type || 'homepage',
    field_id: it.field_id || it.field_name || 'content',
    field_name: it.field_name || it.field_id || 'Content',
    old_value: it.old_value !== undefined ? it.old_value : it.old_data,
    new_value: it.new_value !== undefined ? it.new_value : it.new_data,
    status: it.status || 'pending',
    rejected_reason: it.rejected_reason || it.reason || '',
    submitted_at: it.submitted_at || it.created_at,
    approved_at: it.approved_at || null,
    submitted_by_id: it.submitted_by_id || it.user_id || null,
    submitted_by_name: it.submitted_by_name || it.full_name || 'Website Admin',
    submitted_by_email: it.submitted_by_email || it.email || '',
    approved_by_name: it.approved_by_name || null
  }), []);

  // Load submissions supporting all statuses (with multi-endpoint fallback for both Website Admin & Super Admin)
  const loadSubmissions = useCallback(async (status = 'all') => {
    setLoading(true);
    try {
      const savedToken = localStorage.getItem('ph_auth_token') || localStorage.getItem('auth_token') || sessionStorage.getItem('token');
      const headers = { 'Content-Type': 'application/json' };
      if (savedToken) {
        headers['Authorization'] = `Bearer ${savedToken}`;
      }

      const candidateUrls = [
        `${apiBase}/homepage/cms/field-approvals?status=${status}&limit=200&_t=${Date.now()}`,
        `${apiBase}/admin/cms-submissions?status=${status}&limit=200&_t=${Date.now()}`,
        `${apiBase}/submissions/all?status=${status}&limit=200&_t=${Date.now()}`,
        `${apiBase}/submissions/pending?status=${status}&limit=200&_t=${Date.now()}`,
        `${apiBase}/homepage/cms/pending-approvals?limit=200&_t=${Date.now()}`
      ];

      let loaded = false;
      let lastErrorMessage = '';

      for (const candidateUrl of candidateUrls) {
        try {
          const res = await fetch(candidateUrl, {
            method: 'GET',
            credentials: 'include',
            headers
          });

          if (!res.ok) {
            const errJson = await res.json().catch(() => ({}));
            if (!lastErrorMessage) {
              lastErrorMessage = errJson.error || `HTTP ${res.status}`;
            }
            continue;
          }

          const result = await res.json().catch(() => null);
          if (!result) continue;

          const rawList = Array.isArray(result.data)
            ? result.data
            : Array.isArray(result?.data?.items)
              ? result.data.items
              : null;

          if (!Array.isArray(rawList)) continue;

          const mapped = rawList.map(normalizeSubmissionItem);
          setAllSubmissions(mapped);

          if (result.status_counts) {
            setStatusCounts({
              pending: Number(result.status_counts.pending || 0),
              approved: Number(result.status_counts.approved || 0),
              rejected: Number(result.status_counts.rejected || 0)
            });
          } else {
            const counts = { pending: 0, approved: 0, rejected: 0 };
            mapped.forEach(item => {
              if (counts[item.status] !== undefined) counts[item.status] += 1;
            });
            setStatusCounts(counts);
          }

          setError('');
          loaded = true;
          break;
        } catch (innerErr) {
          if (!lastErrorMessage) {
            lastErrorMessage = innerErr.message || 'Network request failed';
          }
        }
      }

      if (!loaded) {
        setError(lastErrorMessage || 'Failed to load submissions');
      }
    } catch (err) {
      setError(err.message || 'Failed to connect to server');
    } finally {
      setLoading(false);
      if (onRefreshStatsRef.current) {
        onRefreshStatsRef.current();
      }
    }
  }, [apiBase, normalizeSubmissionItem]);

  useEffect(() => {
    loadSubmissions('all');
    loadMediaMap();
    loadTestimonialsMap();
  }, [loadSubmissions, loadMediaMap, loadTestimonialsMap]);

  // On-demand media lookup if selected item contains an unmapped numeric media ID
  useEffect(() => {
    if (!selectedItem) return;
    const extractNumericId = val => {
      const parsed = parseJsonValue(val);
      if (!parsed) return null;
      if (typeof parsed === 'number' || /^\d+$/.test(String(parsed).trim())) {
        return Number(parsed);
      }
      if (typeof parsed === 'object') {
        const img = parsed.image || parsed.photo || parsed.thumbnail || parsed.media_id || parsed.mediaId;
        if (img && /^\d+$/.test(String(img).trim())) {
          return Number(img);
        }
      }
      return null;
    };

    const newId = extractNumericId(selectedItem.new_value);
    const oldId = extractNumericId(selectedItem.old_value);
    const missing = [newId, oldId].filter(id => id && !mediaMap[id] && !mediaMap[String(id)]);
    if (missing.length > 0) {
      loadMediaMap();
    }
  }, [selectedItem, mediaMap, loadMediaMap, parseJsonValue]);

  const filteredSubmissions = useMemo(() => {
    return allSubmissions.filter(item => {
      if (selectedStatusFilter !== 'all' && item.status !== selectedStatusFilter) {
        return false;
      }

      const menu = getMenuCategory(item.page, item.section_type, item.field_name, item.field_id);
      if (selectedMenuFilter !== 'all' && menu.key !== selectedMenuFilter) {
        return false;
      }

      if (!searchQuery.trim()) return true;

      const q = searchQuery.toLowerCase();
      const sectionName = getSectionDisplayName(item.section_type, item.field_name, item.field_id).toLowerCase();
      const fieldName = (item.field_name || item.field_id || '').toLowerCase();
      const submitter = (item.submitted_by_name || item.submitted_by_email || '').toLowerCase();
      const reason = (item.rejected_reason || '').toLowerCase();
      const idMatch = String(item.id).includes(q);

      return sectionName.includes(q) || fieldName.includes(q) || submitter.includes(q) || reason.includes(q) || idMatch;
    });
  }, [allSubmissions, selectedStatusFilter, selectedMenuFilter, searchQuery]);

  // Open details modal and hydrate values if old_value/new_value were omitted by fallback list endpoint
  const openDetailsModal = async item => {
    setSelectedItem(item);
    setShowModal(true);

    const needsDetailHydration = item.old_value === undefined && item.new_value === undefined;
    if (needsDetailHydration) {
      setDetailLoading(true);
      try {
        const savedToken = localStorage.getItem('ph_auth_token') || localStorage.getItem('auth_token') || sessionStorage.getItem('token');
        const headers = { 'Content-Type': 'application/json' };
        if (savedToken) headers['Authorization'] = `Bearer ${savedToken}`;

        const detailUrls = [
          `${apiBase}/homepage/cms/pending-approvals/${item.id}?_t=${Date.now()}`,
          `${apiBase}/submissions/${item.id}?_t=${Date.now()}`
        ];

        for (const detailUrl of detailUrls) {
          const res = await fetch(detailUrl, {
            credentials: 'include',
            headers
          });
          if (res.ok) {
            const json = await res.json();
            if ((json.ok || json.success) && json.data) {
              const d = json.data;
              setSelectedItem(prev => {
                if (!prev || prev.id !== item.id) return prev;
                return {
                  ...prev,
                  old_value: d.old_data !== undefined && d.old_data !== null ? d.old_data : (d.old_value !== undefined ? d.old_value : prev.old_value),
                  new_value: d.new_data !== undefined && d.new_data !== null ? d.new_data : (d.new_value !== undefined ? d.new_value : prev.new_value),
                  rejected_reason: d.rejected_reason || d.reason || prev.rejected_reason
                };
              });
              break;
            }
          }
        }
      } catch (err) {
        console.warn('[ApprovalStatusHistory] Detail fetch warning:', err);
      } finally {
        setDetailLoading(false);
      }
    }
  };

  const closeModal = () => {
    setShowModal(false);
    setSelectedItem(null);
    setDetailLoading(false);
    setLightboxImage(null);
  };

  const resolveImage = useCallback(
    val => {
      if (!val) return null;
      const origin = (apiBase || '').replace(/\/api\/?$/, '');

      const withOrigin = path => {
        if (!path) return null;
        if (path.startsWith('http://') || path.startsWith('https://') || path.startsWith('data:image/')) {
          return path;
        }
        if (path.startsWith('/')) {
          return origin ? `${origin}${path}` : path;
        }
        return origin ? `${origin}/${path}` : `/${path}`;
      };

      if (typeof val === 'object') {
        const candidate = val.image || val.image_url || val.photo || val.thumbnail || val.logo || val.url || val.file_path || val.filename || val.file_name;
        if (candidate && typeof candidate === 'string') {
          return resolveImage(candidate);
        }
        return null;
      }

      if (typeof val === 'string') {
        const trimmed = val.trim();
        if (trimmed.length > 500 || trimmed.includes('\n')) {
          return null;
        }
        if (trimmed.includes(' ') && !trimmed.match(/^[a-zA-Z0-9_\-\s/.:]+\.(jpg|jpeg|png|webp|svg|gif|avif)$/i)) {
          return null;
        }
        if (trimmed.startsWith('http://') || trimmed.startsWith('https://') || trimmed.startsWith('data:image/')) {
          return trimmed;
        }
        if (mediaMap[trimmed]) {
          return withOrigin(mediaMap[trimmed]);
        }
        if (trimmed.startsWith('/api/uploads/')) {
          return withOrigin(trimmed);
        }
        if (trimmed.startsWith('/uploads/')) {
          return withOrigin(`/api${trimmed}`);
        }
        if (trimmed.startsWith('uploads/')) {
          return withOrigin(`/api/${trimmed}`);
        }
        if (trimmed.startsWith('admissions_')) {
          return withOrigin(`/api/uploads/admissions/${trimmed}`);
        }
        if (trimmed.startsWith('studentlife_') || trimmed.startsWith('student_life_') || trimmed.startsWith('life_')) {
          return withOrigin(`/api/uploads/studentlife/${trimmed}`);
        }
        if (trimmed.startsWith('education_')) {
          return withOrigin(`/api/uploads/homepage/education/${trimmed}`);
        }
        if (trimmed.startsWith('aboutus_')) {
          return withOrigin(`/api/uploads/homepage/about-us/${trimmed}`);
        }
        if (trimmed.startsWith('media_')) {
          return withOrigin(`/api/uploads/homepage/highlights/${trimmed}`);
        }
        if (trimmed.startsWith('cms_media_')) {
          return withOrigin(`/uploads/cms/${trimmed}`);
        }
        if (trimmed.match(/\.(jpg|jpeg|png|webp|svg|gif|avif)$/i)) {
          const sType = (selectedItem?.section_type || '').toLowerCase();
          const pType = (selectedItem?.page || '').toLowerCase();
          if (sType.includes('admissions') || pType.includes('admissions') || trimmed.includes('admission')) {
            return withOrigin(`/api/uploads/admissions/${trimmed}`);
          }
          if (sType.includes('studentlife') || pType.includes('studentlife') || trimmed.includes('studentlife')) {
            return withOrigin(`/api/uploads/studentlife/${trimmed}`);
          }
          if (sType.includes('education') || pType.includes('education') || trimmed.includes('education')) {
            return withOrigin(`/api/uploads/homepage/education/${trimmed}`);
          }
          if (sType.includes('aboutus') || pType.includes('aboutus') || trimmed.includes('aboutus')) {
            return withOrigin(`/api/uploads/homepage/about-us/${trimmed}`);
          }
          return withOrigin(`/api/uploads/images/${trimmed}`);
        }
      }
      if (typeof val === 'number' || /^\d+$/.test(String(val).trim())) {
        const idKey = String(val).trim();
        if (mediaMap[idKey]) return withOrigin(mediaMap[idKey]);
        if (mediaMap[Number(idKey)]) return withOrigin(mediaMap[Number(idKey)]);
      }
      return null;
    },
    [mediaMap, apiBase, selectedItem]
  );

  const PRESET_LINK_DESTINATIONS = {
    'register': {
      title: 'New Student Registration Form',
      route: '/register',
      category: 'Registration & Admissions',
      badgeColor: '#2563eb',
      badgeBg: '#eff6ff',
      isExternal: false
    },
    'contact-us#contact-form': {
      title: 'Contact Us - Inquiry Form',
      route: '/contact-us#contact-form',
      category: 'Inquiry & Support',
      badgeColor: '#059669',
      badgeBg: '#ecfdf5',
      isExternal: false
    },
    'contact-us': {
      title: 'Contact Us Main Page',
      route: '/contact-us',
      category: 'Contact Page',
      badgeColor: '#059669',
      badgeBg: '#ecfdf5',
      isExternal: false
    },
    'admissions': {
      title: 'Admissions & Enrollment Information',
      route: '/admissions',
      category: 'Admissions Pathway',
      badgeColor: '#7c3aed',
      badgeBg: '#f5f3ff',
      isExternal: false
    },
    'education-program': {
      title: 'Education Program & Academic Pathways',
      route: '/education-program',
      category: 'Academic Curriculum',
      badgeColor: '#0284c7',
      badgeBg: '#f0f9ff',
      isExternal: false
    },
    'student-life': {
      title: 'Student Life, Clubs & ECA',
      route: '/student-life',
      category: 'Student Activities',
      badgeColor: '#db2777',
      badgeBg: '#fdf2f8',
      isExternal: false
    },
    'facilities': {
      title: 'Campus Facilities Overview',
      route: '/facilities',
      category: 'Campus Facilities',
      badgeColor: '#0d9488',
      badgeBg: '#f0fdfa',
      isExternal: false
    },
    'news-and-updates': {
      title: 'News & Announcements',
      route: '/news-and-updates',
      category: 'News & Events',
      badgeColor: '#ea580c',
      badgeBg: '#fff7ed',
      isExternal: false
    },
    'parents': {
      title: 'Parents Portal & Handbook',
      route: '/parents',
      category: 'Parent Community',
      badgeColor: '#b45309',
      badgeBg: '#fffbeb',
      isExternal: false
    },
    'careers': {
      title: 'Careers & Job Openings',
      route: '/careers',
      category: 'Career Opportunities',
      badgeColor: '#4f46e5',
      badgeBg: '#eef2ff',
      isExternal: false
    },
    'about-us': {
      title: 'About Us & School Profile',
      route: '/about-us',
      category: 'School Profile',
      badgeColor: '#475569',
      badgeBg: '#f8fafc',
      isExternal: false
    },
    'login': {
      title: 'PHS Portal Login Gateway',
      route: '/login',
      category: 'Portal Authentication',
      badgeColor: '#1e293b',
      badgeBg: '#f1f5f9',
      isExternal: false
    },
    '#school-calendar': {
      title: 'School Academic Calendar Section',
      route: '#school-calendar',
      category: 'In-Page Jump Section',
      badgeColor: '#d97706',
      badgeBg: '#fffbeb',
      isExternal: false
    }
  };

  const resolveLinkDestination = rawLink => {
    if (!rawLink || typeof rawLink !== 'string') {
      return {
        title: '(No Destination Configured)',
        route: '',
        category: 'Unset',
        badgeColor: '#94a3b8',
        badgeBg: '#f1f5f9',
        isExternal: false
      };
    }

    const trimmed = rawLink.trim();
    const normalized = trimmed.startsWith('/') ? trimmed.slice(1) : trimmed;

    if (PRESET_LINK_DESTINATIONS[normalized]) {
      return PRESET_LINK_DESTINATIONS[normalized];
    }

    if (trimmed.startsWith('http://') || trimmed.startsWith('https://')) {
      let hostname = trimmed;
      try {
        hostname = new URL(trimmed).hostname;
      } catch (_) {}
      return {
        title: `External Website (${hostname})`,
        route: trimmed,
        category: 'External Link',
        badgeColor: '#2563eb',
        badgeBg: '#eff6ff',
        isExternal: true
      };
    }

    if (trimmed.startsWith('#')) {
      return {
        title: `In-Page Anchor: ${trimmed}`,
        route: trimmed,
        category: 'In-Page Section',
        badgeColor: '#d97706',
        badgeBg: '#fffbeb',
        isExternal: false
      };
    }

    return {
      title: `Internal Route: /${normalized}`,
      route: `/${normalized}`,
      category: 'Internal Page',
      badgeColor: '#475569',
      badgeBg: '#f8fafc',
      isExternal: false
    };
  };

  const getFieldMeta = key => {
    const kLower = (key || '').toLowerCase();
    if (kLower === 'tagline_prefix') return { label: 'Tagline Prefix', icon: Type, isLink: false };
    if (kLower === 'tagline_accent') return { label: 'Tagline Accent', icon: Sparkles, isLink: false };
    if (kLower === 'philosophy') return { label: 'Philosophy Statement', icon: AlignLeft, isLink: false };
    if (kLower === 'video_file' || kLower === 'video_url') return { label: 'Hero Video', icon: VideoIcon, isLink: false };
    if (kLower === 'principal_photo_id' || kLower === 'principal_photo') return { label: 'Principal Portrait Photo', icon: ImageIcon, isLink: false };
    if (kLower === 'title') return { label: 'Title / Headline', icon: Type, isLink: false };
    if (kLower === 'text' || kLower === 'description' || kLower === 'desc' || kLower === 'content') return { label: 'Content / Description', icon: AlignLeft, isLink: false };
    if (kLower === 'link' || kLower === 'url' || kLower === 'href' || kLower === 'button_link' || kLower === 'buttonlink' || kLower === 'path') return { label: 'Link Destination', icon: Link2, isLink: true };
    if (kLower === 'button_text' || kLower === 'buttontext' || kLower === 'btn_text') return { label: 'Button Label', icon: Tag, isLink: false };
    if (kLower === 'subtitle' || kLower === 'sub_title') return { label: 'Subtitle', icon: Type, isLink: false };
    if (kLower === 'countdown_date' || kLower === 'target_date') return { label: 'Target Opening Date', icon: Calendar, isLink: false };
    if (kLower === 'countdown_label') return { label: 'Pill Badge Text', icon: Tag, isLink: false };
    if (kLower === 'countdown_title') return { label: 'Countdown Title', icon: Type, isLink: false };
    if (kLower === 'countdown_description') return { label: 'Countdown Description', icon: AlignLeft, isLink: false };
    if (kLower === 'kicker') return { label: 'Kicker / Eyebrow', icon: Tag, isLink: false };
    if (kLower === 'latest_news_label' || kLower === 'latestnewslabel') return { label: 'Card Header Label', icon: Tag, isLink: false };
    if (kLower === 'intro') return { label: 'Introduction Summary', icon: AlignLeft, isLink: false };
    if (kLower === 'badge' || kLower === 'tag') return { label: 'Category Tag / Kicker', icon: Tag, isLink: false };
    if (kLower === 'icon') return { label: 'Icon Name', icon: Sparkles, isLink: false };
    if (kLower === 'name' || kLower === 'author' || kLower === 'author_name') return { label: 'Name / Author', icon: User, isLink: false };
    if (kLower === 'role' || kLower === 'author_type') return { label: 'Role / Category', icon: User, isLink: false };
    if (kLower === 'date') return { label: 'Date', icon: Calendar, isLink: false };
    if (kLower === 'points' || kLower === 'point') return { label: 'Points / Elements', icon: List, isLink: false };
    if (kLower === 'facts' || kLower === 'fact' || kLower === 'statistics' || kLower === 'stats') return { label: 'Facts & Statistics', icon: List, isLink: false };
    if (kLower === 'milestones' || kLower === 'milestone') return { label: 'Timeline Milestones', icon: Calendar, isLink: false };
    if (kLower === 'tags') return { label: 'Tags / Categories', icon: Tag, isLink: false };
    if (kLower === 'cta') return { label: 'Call to Action / Footer Note', icon: AlignLeft, isLink: false };
    if (kLower === 'headline') return { label: 'Headline', icon: Type, isLink: false };
    if (kLower === 'paragraph') return { label: 'Main Paragraph / Story', icon: AlignLeft, isLink: false };
    if (kLower === 'bio' || kLower === 'biography') return { label: 'Professional Biography', icon: AlignLeft, isLink: false };
    if (kLower === 'note') return { label: 'Special Note / Footnote', icon: AlignLeft, isLink: false };
    if (kLower === 'quote') return { label: 'Quote Statement', icon: AlignLeft, isLink: false };
    if (kLower === 'shorttitle' || kLower === 'short_title') return { label: 'Pill Tab Label', icon: Tag, isLink: false };
    return {
      label: key.replace(/_/g, ' ').replace(/\b\w/g, c => c.toUpperCase()),
      icon: FileText,
      isLink: kLower.includes('link') || kLower.includes('url') || kLower.includes('href')
    };
  };

  const renderLinkDestinationPreview = (rawLink, isDifferent, isBefore) => {
    const info = resolveLinkDestination(rawLink);

    return (
      <div
        className="preview-link-card"
        style={{
          display: 'flex',
          flexDirection: 'column',
          gap: '6px',
          padding: '10px 14px',
          borderRadius: '8px',
          background: isDifferent ? (isBefore ? '#fff5f5' : '#f0fdf4') : '#f8fafc',
          border: `1.5px solid ${isDifferent ? (isBefore ? '#fca5a5' : '#86efac') : '#e2e8f0'}`,
          borderLeft: `4px solid ${isDifferent ? (isBefore ? '#ef4444' : '#10b981') : info.badgeColor}`,
          marginTop: '4px'
        }}
      >
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: '8px', flexWrap: 'wrap' }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
            <Link2 size={14} style={{ color: info.badgeColor, flexShrink: 0 }} />
            <strong style={{ fontSize: '13px', color: isDifferent ? (isBefore ? '#991b1b' : '#166534') : '#0f172a' }}>
              {info.title}
            </strong>
          </div>

          <span
            style={{
              fontSize: '10.5px',
              fontWeight: '700',
              padding: '2px 8px',
              borderRadius: '999px',
              background: info.badgeBg,
              color: info.badgeColor,
              border: `1px solid ${info.badgeColor}33`,
              textTransform: 'uppercase',
              letterSpacing: '0.02em'
            }}
          >
            {info.category}
          </span>
        </div>

        <div style={{ display: 'flex', alignItems: 'center', gap: '8px', fontSize: '12px', color: '#64748b', flexWrap: 'wrap' }}>
          <span style={{ fontWeight: '600' }}>Destination:</span>
          <code
            style={{
              background: '#ffffff',
              padding: '2px 8px',
              borderRadius: '4px',
              border: '1px solid #cbd5e1',
              color: '#1e293b',
              fontSize: '11.5px',
              fontFamily: 'monospace'
            }}
          >
            {info.route || '(empty)'}
          </code>
          {info.isExternal && (
            <span style={{ display: 'inline-flex', alignItems: 'center', gap: '3px', color: '#2563eb', fontSize: '11px', fontWeight: '600' }}>
              <ExternalLink size={11} />
              <span>Opens in new tab</span>
            </span>
          )}
        </div>
      </div>
    );
  };

  /**
   * Dedicated rich renderer for Testimonial Publish / Unpublish / Delete submissions
   * Always resolves and displays the full testimonial review text on both Before and After sides!
   */
  const renderTestimonialTogglePreview = (rawValue, isBefore, itemContext) => {
    const parsedVal = parseJsonValue(itemContext?.new_value);
    const parsedOld = parseJsonValue(itemContext?.old_value);
    const currentParsed = parseJsonValue(rawValue);

    const extractedId =
      parsedVal?.id ||
      parsedOld?.id ||
      itemContext?.field_id?.match(/testimonials\.(\d+)/i)?.[1] ||
      null;

    const extractedAuthorRaw =
      parsedVal?.author_name ||
      parsedOld?.author_name ||
      itemContext?.field_name?.match(/:\s*(.+)$/)?.[1] ||
      '';

    const matchedRecord =
      (extractedId && testimonialsMap.byId[String(extractedId)]) ||
      (extractedAuthorRaw && testimonialsMap.byAuthor[String(extractedAuthorRaw).trim().toLowerCase()]) ||
      null;

    const authorName =
      currentParsed?.author_name ||
      parsedVal?.author_name ||
      matchedRecord?.author_name ||
      extractedAuthorRaw ||
      'Testimonial Author';

    const authorType =
      currentParsed?.author_type ||
      parsedVal?.author_type ||
      matchedRecord?.author_type ||
      'Parent / Student';

    const quoteText =
      currentParsed?.content ||
      currentParsed?.quote ||
      parsedVal?.content ||
      parsedVal?.quote ||
      parsedOld?.content ||
      matchedRecord?.content ||
      '';

    const rawImg =
      currentParsed?.image_url ||
      parsedVal?.image_url ||
      matchedRecord?.image_url ||
      null;
    const resolvedImg = rawImg ? resolveImage(rawImg) : null;

    const actionType = (
      parsedVal?.action ||
      (itemContext?.field_id?.toLowerCase().includes('unpublish') || itemContext?.field_name?.toLowerCase().includes('unpublish')
        ? 'unpublish'
        : itemContext?.field_id?.toLowerCase().includes('delete') || itemContext?.field_name?.toLowerCase().includes('delete')
          ? 'delete'
          : 'publish')
    ).toLowerCase();

    const isDeactivating = actionType === 'unpublish' || actionType === 'delete';

    if (isBefore) {
      const wasApproved =
        String(rawValue || '').toLowerCase() === 'approved' ||
        (rawValue === null || rawValue === undefined ? isDeactivating : false);

      return (
        <div className="preview-object-container">
          <div
            style={{
              display: 'flex',
              flexDirection: 'column',
              gap: '12px',
              padding: '16px',
              borderRadius: '10px',
              backgroundColor: '#ffffff',
              border: '1.5px solid #cbd5e1',
              borderLeft: `4px solid ${wasApproved ? '#059669' : '#d97706'}`
            }}
          >
            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', flexWrap: 'wrap', gap: '8px' }}>
              <span style={{ fontSize: '11px', fontWeight: '700', color: '#64748b', textTransform: 'uppercase', letterSpacing: '0.05em' }}>
                Current Public Website Status
              </span>
              <span
                style={{
                  fontSize: '11px',
                  fontWeight: '800',
                  padding: '3px 10px',
                  borderRadius: '999px',
                  backgroundColor: wasApproved ? '#dcfce7' : '#fef3c7',
                  color: wasApproved ? '#166534' : '#92400e',
                  border: `1px solid ${wasApproved ? '#86efac' : '#fde68a'}`
                }}
              >
                {wasApproved ? 'PUBLISHED (ON) - LIVE ON WEBSITE' : 'UNPUBLISHED (OFF) - HIDDEN'}
              </span>
            </div>

            <div style={{ display: 'flex', alignItems: 'center', gap: '8px', fontSize: '13.5px', color: '#0f172a' }}>
              <User size={14} style={{ color: '#64748b', flexShrink: 0 }} />
              <span>
                Author: <strong>{authorName}</strong>{' '}
                <span style={{ color: '#64748b', textTransform: 'capitalize' }}>({authorType})</span>
              </span>
            </div>

            <div
              style={{
                padding: '14px 16px',
                borderRadius: '8px',
                backgroundColor: '#f8fafc',
                border: '1px solid #e2e8f0'
              }}
            >
              <div style={{ display: 'flex', alignItems: 'center', gap: '6px', marginBottom: '8px' }}>
                <Quote size={14} style={{ color: '#2563eb', flexShrink: 0 }} />
                <span style={{ fontSize: '11px', fontWeight: '700', color: '#1e40af', textTransform: 'uppercase', letterSpacing: '0.04em' }}>
                  {wasApproved ? 'Active Testimonial Text on Public Website:' : 'Current Testimonial Review Text:'}
                </span>
              </div>
              <p
                style={{
                  margin: 0,
                  fontSize: '14.5px',
                  lineHeight: '1.65',
                  color: '#0f172a',
                  fontStyle: 'italic',
                  fontWeight: '500',
                  whiteSpace: 'pre-wrap',
                  wordBreak: 'break-word'
                }}
              >
                {quoteText ? `"${quoteText}"` : '(Testimonial text is empty)'}
              </p>
            </div>

            {resolvedImg && (
              <SafeImagePreview
                src={resolvedImg}
                rawMediaRef={String(rawImg)}
                alt={authorName}
                isBefore={true}
                isImgDifferent={false}
                onOpenLightbox={(src, title) => setLightboxImage({ src, title })}
              />
            )}
          </div>
        </div>
      );
    }

    return (
      <div className="preview-object-container">
        <div
          style={{
            display: 'flex',
            flexDirection: 'column',
            gap: '12px',
            padding: '16px',
            borderRadius: '10px',
            backgroundColor: isDeactivating ? '#fff5f5' : '#f0fdf4',
            border: `1.5px solid ${isDeactivating ? '#fca5a5' : '#86efac'}`,
            borderLeft: `4px solid ${isDeactivating ? '#ef4444' : '#10b981'}`
          }}
        >
          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', flexWrap: 'wrap', gap: '8px' }}>
            <span
              style={{
                fontSize: '11px',
                fontWeight: '700',
                color: isDeactivating ? '#991b1b' : '#166534',
                textTransform: 'uppercase',
                letterSpacing: '0.05em'
              }}
            >
              Proposed Status Change
            </span>
            <span
              style={{
                fontSize: '11px',
                fontWeight: '800',
                padding: '3px 10px',
                borderRadius: '999px',
                backgroundColor: isDeactivating ? '#fee2e2' : '#dcfce7',
                color: isDeactivating ? '#991b1b' : '#166534',
                border: `1px solid ${isDeactivating ? '#fca5a5' : '#86efac'}`
              }}
            >
              {actionType === 'delete'
                ? 'DELETE TESTIMONIAL'
                : isDeactivating
                  ? 'UNPUBLISH (OFF) - DEACTIVATE'
                  : 'PUBLISH (ON) - ACTIVATE'}
            </span>
          </div>

          <div style={{ display: 'flex', alignItems: 'center', gap: '8px', fontSize: '13.5px', color: '#0f172a' }}>
            <User size={14} style={{ color: isDeactivating ? '#b91c1c' : '#15803d', flexShrink: 0 }} />
            <span>
              Author: <strong>{authorName}</strong>{' '}
              <span style={{ color: '#64748b', textTransform: 'capitalize' }}>({authorType})</span>
            </span>
          </div>

          <div style={{ fontSize: '12.5px', color: isDeactivating ? '#991b1b' : '#166534', lineHeight: '1.5' }}>
            {actionType === 'delete'
              ? 'This testimonial review text will be permanently deleted upon Principal approval:'
              : isDeactivating
                ? 'This testimonial review text will be deactivated (OFF) and hidden from the public website upon Principal approval:'
                : 'This testimonial review text will be activated (ON) and published on the public website upon Principal approval:'}
          </div>

          <div
            style={{
              padding: '14px 16px',
              borderRadius: '8px',
              backgroundColor: '#ffffff',
              border: `1.5px solid ${isDeactivating ? '#fca5a5' : '#86efac'}`
            }}
          >
            <div style={{ display: 'flex', alignItems: 'center', gap: '6px', marginBottom: '8px' }}>
              <Quote size={14} style={{ color: isDeactivating ? '#dc2626' : '#059669', flexShrink: 0 }} />
              <span
                style={{
                  fontSize: '11px',
                  fontWeight: '700',
                  color: isDeactivating ? '#991b1b' : '#166534',
                  textTransform: 'uppercase',
                  letterSpacing: '0.04em'
                }}
              >
                {actionType === 'delete'
                  ? 'Testimonial Text to Be Deleted:'
                  : isDeactivating
                    ? 'Testimonial Text Being Deactivated (OFF):'
                    : 'Testimonial Text Being Published (ON):'}
              </span>
            </div>
            <p
              style={{
                margin: 0,
                fontSize: '14.5px',
                lineHeight: '1.65',
                color: '#0f172a',
                fontStyle: 'italic',
                fontWeight: '600',
                whiteSpace: 'pre-wrap',
                wordBreak: 'break-word'
              }}
            >
              {quoteText ? `"${quoteText}"` : '(Testimonial text is empty)'}
            </p>
          </div>

          {resolvedImg && (
            <SafeImagePreview
              src={resolvedImg}
              rawMediaRef={String(rawImg)}
              alt={authorName}
              isBefore={false}
              isImgDifferent={false}
              onOpenLightbox={(src, title) => setLightboxImage({ src, title })}
            />
          )}
        </div>

        {typeof currentParsed === 'object' && currentParsed !== null && (
          <details className="raw-json-details">
            <summary>View Raw Data</summary>
            <pre className="raw-json-code">{JSON.stringify(currentParsed, null, 2)}</pre>
          </details>
        )}
      </div>
    );
  };

  /**
   * Comprehensive content value renderer for Before & After with visual diff highlighting
   */
  const renderValuePreview = (rawValue, fieldId = '', isBefore = false, otherValue = null) => {
    const parsed = parseJsonValue(rawValue);
    const otherParsed = parseJsonValue(otherValue);

    // Check if this submission is a Testimonial status toggle (Publish / Unpublish / Delete)
    const fidLower = (fieldId || '').toLowerCase();
    const fnameLower = (selectedItem?.field_name || '').toLowerCase();
    const isTestimonialAction =
      fidLower.startsWith('testimonials.') ||
      (parsed && typeof parsed === 'object' && (parsed.action === 'unpublish' || parsed.action === 'publish')) ||
      (otherParsed && typeof otherParsed === 'object' && (otherParsed.action === 'unpublish' || otherParsed.action === 'publish')) ||
      fnameLower.includes('unpublish (off)') ||
      fnameLower.includes('publish (on)');

    if (isTestimonialAction) {
      return renderTestimonialTogglePreview(rawValue, isBefore, selectedItem);
    }

    const isOtherEmpty =
      otherParsed === null ||
      otherParsed === undefined ||
      otherParsed === '' ||
      (typeof otherParsed === 'object' && Object.keys(otherParsed || {}).length === 0);
    const op = getOperationInfo(fieldId, isBefore ? rawValue : otherValue, isBefore ? otherValue : rawValue, selectedItem?.field_name);

    if (
      parsed === null ||
      parsed === undefined ||
      parsed === '' ||
      (typeof parsed === 'object' && Object.keys(parsed || {}).length === 0)
    ) {
      if (isBefore) {
        return (
          <div className="empty-value-box" style={{ background: '#f8fafc', border: '1.5px dashed #cbd5e1', padding: '24px 16px', borderRadius: '10px', flexDirection: 'column' }}>
            <PlusCircle size={22} style={{ color: '#16a34a', marginBottom: '4px' }} />
            <strong style={{ fontSize: '13px', color: '#166534', fontStyle: 'normal' }}>New Item Addition</strong>
            <span style={{ fontSize: '12px', color: '#64748b', fontStyle: 'normal' }}>No previous value on the public website (New Item).</span>
          </div>
        );
      }
      if (op.type === 'delete') {
        return (
          <div className="empty-value-box" style={{ background: '#fef2f2', border: '1.5px dashed #fca5a5', padding: '24px 16px', borderRadius: '10px', flexDirection: 'column' }}>
            <Trash2 size={22} style={{ color: '#dc2626', marginBottom: '4px' }} />
            <strong style={{ fontSize: '13px', color: '#991b1b', fontStyle: 'normal' }}>Item Deletion Request</strong>
            <span style={{ fontSize: '12px', color: '#b91c1c', fontStyle: 'normal' }}>This item will be removed from the public website once approved.</span>
          </div>
        );
      }
      return (
        <div className="empty-value-box">
          <FileText size={18} className="empty-icon" />
          <span>Value cleared (Empty)</span>
        </div>
      );
    }

    // 1. Direct Video
    const fLower = (fieldId || '').toLowerCase();
    const isVideoField = fLower.includes('video');
    if (isVideoField && typeof parsed === 'string') {
      const videoSrc = parsed.startsWith('http') || parsed.startsWith('/') ? parsed : `/api/uploads/videos/${parsed}`;
      return (
        <div className="preview-video-container">
          <video src={videoSrc} controls className="preview-video-player" />
          <div className="preview-media-caption">
            <VideoIcon size={14} />
            <span>Video File: {parsed}</span>
          </div>
        </div>
      );
    }

    // 2. Direct Image
    const isImageField =
      fLower.includes('image') ||
      fLower.includes('photo') ||
      fLower.includes('thumb') ||
      fLower.includes('pic') ||
      fLower.includes('logo') ||
      fLower.includes('banner') ||
      fLower.includes('cover') ||
      fLower.includes('media_id') ||
      fLower.includes('mediaid') ||
      fLower.includes('gallery');
    const isKnownImageFormat =
      typeof parsed === 'string' && (parsed.startsWith('data:image/') || !!parsed.match(/\.(jpg|jpeg|png|webp|svg|gif|avif)$/i));

    if ((isImageField || isKnownImageFormat) && (typeof parsed === 'string' || typeof parsed === 'number')) {
      const resolvedUrl = resolveImage(parsed);
      const isImgDifferent = isOtherEmpty || (otherParsed !== null && otherParsed !== undefined && String(parsed) !== String(otherParsed));

      return (
        <SafeImagePreview
          src={resolvedUrl}
          rawMediaRef={String(parsed)}
          alt="Field Media"
          isBefore={isBefore}
          isImgDifferent={isImgDifferent}
          onOpenLightbox={(src, title) => setLightboxImage({ src, title })}
        />
      );
    }

    // 3. Array of items
    if (Array.isArray(parsed)) {
      if (parsed.length === 0) {
        return <div className="empty-value-box">Empty list (0 items)</div>;
      }
      return (
        <div className="preview-array-list">
          <div className="array-count-badge">Total: {parsed.length} items</div>
          {parsed.map((item, idx) => {
            if (typeof item === 'object' && item !== null) {
              const itemImg =
                item.image ||
                item.photo ||
                item.thumbnail ||
                item.logo ||
                item.media_id ||
                item.mediaId ||
                (Array.isArray(item.images) ? item.images[0] : null);
              const resolvedItemImg = resolveImage(itemImg);
              const hasLabel = item.label !== undefined || item.key !== undefined;
              const hasValue = item.value !== undefined || item.val !== undefined;
              const hasLabelValue = hasLabel && hasValue;
              const itemLabel = item.label || item.key;
              const itemValue = item.value !== undefined ? item.value : item.val;
              const itemHeading =
                item.title ||
                item.name ||
                item.question ||
                item.headline ||
                item.heading ||
                (hasLabel ? itemLabel : null) ||
                (item.year ? `Year ${item.year}` : `Item ${idx + 1}`);
              const itemDetail =
                item.description || item.desc || item.answer || item.detail || item.text || item.content || item.paragraph || item.bio;

              return (
                <div key={idx} className="preview-card-item">
                  <div className="card-item-header" style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: '8px', flexWrap: 'wrap' }}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
                      <span className="card-idx-tag">#{idx + 1}</span>
                      <strong className="card-item-title">{itemHeading}</strong>
                    </div>
                    {hasLabelValue && (
                      <span
                        style={{
                          fontWeight: '700',
                          fontSize: '12px',
                          color: isBefore ? '#7f1d1d' : '#047857',
                          background: isBefore ? '#fecaca' : '#a7f3d0',
                          padding: '2px 8px',
                          borderRadius: '4px'
                        }}
                      >
                        {String(itemValue)}
                      </span>
                    )}
                    {item.year && !hasLabelValue && (
                      <span
                        style={{
                          fontWeight: '700',
                          fontSize: '11px',
                          color: isBefore ? '#7f1d1d' : '#047857',
                          background: isBefore ? '#fee2e2' : '#dcfce7',
                          padding: '2px 6px',
                          borderRadius: '4px'
                        }}
                      >
                        Year {String(item.year)}
                      </span>
                    )}
                  </div>
                  {resolvedItemImg && (
                    <img
                      src={resolvedItemImg}
                      alt="Thumbnail"
                      className="card-item-thumb"
                      onError={e => {
                        e.target.style.display = 'none';
                      }}
                    />
                  )}
                  {item.subtitle && <div className="card-item-sub">{item.subtitle}</div>}
                  {itemDetail && <div className="card-item-desc">{itemDetail}</div>}
                  {item.link && renderLinkDestinationPreview(item.link, false, isBefore)}
                </div>
              );
            }

            const itemStr = String(item || '').trim();
            const isItemImage =
              isImageField ||
              itemStr.startsWith('data:image/') ||
              !!itemStr.match(/\.(jpg|jpeg|png|webp|svg|gif|avif)($|\?)/i) ||
              itemStr.includes('/uploads/');
            const resolvedItemImg = isItemImage ? resolveImage(itemStr) : null;

            if (resolvedItemImg) {
              const otherArr = Array.isArray(otherParsed) ? otherParsed : [];
              const otherItemStr = String(otherArr[idx] || '').trim();
              const isImgDiff = isOtherEmpty || otherArr[idx] === undefined || itemStr !== otherItemStr;

              return (
                <div key={idx} className="preview-card-item" style={{ marginBottom: '12px' }}>
                  <div className="card-item-header" style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: '8px', gap: '8px' }}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
                      <span className="card-idx-tag">Photo #{idx + 1}</span>
                    </div>
                    <span style={{ fontSize: '11px', color: '#64748b', wordBreak: 'break-all', maxWidth: '70%', textAlign: 'right' }}>
                      {itemStr.split('/').pop()}
                    </span>
                  </div>
                  <SafeImagePreview
                    src={resolvedItemImg}
                    rawMediaRef={itemStr}
                    alt={`Photo Preview #${idx + 1}`}
                    isBefore={isBefore}
                    isImgDifferent={isImgDiff}
                    onOpenLightbox={(src, title) => setLightboxImage({ src, title })}
                  />
                </div>
              );
            }

            return (
              <div key={idx} className="preview-simple-pill">
                #{idx + 1}: {String(item)}
              </div>
            );
          })}
        </div>
      );
    }

    // 4. Structured Object
    if (typeof parsed === 'object') {
      const objImg = parsed.image || parsed.image_url || parsed.photo || parsed.thumbnail || parsed.logo || parsed.media_id || parsed.mediaId || parsed.url;
      const resolvedObjImg = resolveImage(objImg);
      const otherImg =
        otherParsed && typeof otherParsed === 'object'
          ? otherParsed.image || otherParsed.image_url || otherParsed.photo || otherParsed.thumbnail || otherParsed.logo || otherParsed.media_id || otherParsed.mediaId || otherParsed.url
          : null;
      const isImgDifferent = isOtherEmpty || (otherParsed !== null && otherParsed !== undefined && String(objImg || '') !== String(otherImg || ''));

      return (
        <div className="preview-object-container">
          {resolvedObjImg ? (
            <SafeImagePreview
              src={resolvedObjImg}
              rawMediaRef={String(objImg)}
              alt="Slide / Item Media"
              isBefore={isBefore}
              isImgDifferent={isImgDifferent}
              onOpenLightbox={(src, title) => setLightboxImage({ src, title })}
            />
          ) : objImg ? (
            <div
              className={`preview-image-container mb-3 ${isImgDifferent ? (isBefore ? 'field-card-diff-before' : 'field-card-diff-after') : ''}`}
              style={{
                width: '100%',
                boxSizing: 'border-box',
                borderRadius: '10px',
                padding: '16px',
                border: isImgDifferent ? (isBefore ? '2px solid #ef4444' : '2px solid #10b981') : '1.5px dashed #cbd5e1',
                backgroundColor: isImgDifferent ? (isBefore ? '#fef2f2' : '#f0fdf4') : '#f8fafc',
                textAlign: 'center'
              }}
            >
              <ImageIcon size={32} style={{ color: isImgDifferent ? '#10b981' : '#94a3b8', margin: '0 auto 8px' }} />
              <div style={{ fontSize: '13px', fontWeight: '700', color: '#1e293b' }}>
                Photo Reference: {String(objImg)}
              </div>
            </div>
          ) : null}

          <div className="preview-kv-grid" style={{ display: 'flex', flexDirection: 'column', gap: '10px', background: 'transparent', padding: 0, border: 'none' }}>
            {Object.entries(parsed).map(([k, v]) => {
              if (
                k === 'image' ||
                k === 'image_url' ||
                k === 'photo' ||
                k === 'thumbnail' ||
                k === 'logo' ||
                k === 'media_id' ||
                k === 'mediaId' ||
                k === 'id' ||
                k === '_id' ||
                k === 'key'
              ) {
                return null;
              }
              const meta = getFieldMeta(k);
              const FieldIcon = meta.icon;
              const otherVal = otherParsed && typeof otherParsed === 'object' ? otherParsed[k] : undefined;
              const isDifferent =
                isOtherEmpty ||
                (otherParsed !== null && otherParsed !== undefined && typeof otherParsed === 'object' && JSON.stringify(v) !== JSON.stringify(otherVal));
              const isValNull = v === null || v === undefined || v === 'null';
              const valStr = isValNull ? '' : (typeof v === 'object' ? JSON.stringify(v) : String(v));
              const tagLabel = isBefore ? 'ORIGINAL' : (isOtherEmpty ? 'NEW' : 'UPDATED');

              return (
                <div
                  key={k}
                  className={`preview-field-card ${isDifferent ? (isBefore ? 'field-card-diff-before' : 'field-card-diff-after') : ''}`}
                  style={{
                    display: 'flex',
                    flexDirection: 'column',
                    gap: '6px',
                    padding: '12px 14px',
                    borderRadius: '8px',
                    backgroundColor: isDifferent ? (isBefore ? '#fef2f2' : '#f0fdf4') : '#ffffff',
                    border: `1.5px solid ${isDifferent ? (isBefore ? '#fca5a5' : '#86efac') : '#e2e8f0'}`,
                    borderLeft: `4px solid ${isDifferent ? (isBefore ? '#ef4444' : '#10b981') : '#94a3b8'}`,
                    boxShadow: '0 1px 3px rgba(0,0,0,0.02)'
                  }}
                >
                  <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: '8px' }}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
                      <FieldIcon size={12} style={{ color: isDifferent ? (isBefore ? '#b91c1c' : '#15803d') : '#64748b' }} />
                      <span
                        style={{
                          fontSize: '11px',
                          fontWeight: '700',
                          textTransform: 'uppercase',
                          letterSpacing: '0.04em',
                          color: isDifferent ? (isBefore ? '#991b1b' : '#166534') : '#64748b'
                        }}
                      >
                        {meta.label}
                      </span>
                    </div>

                    {isDifferent && (
                      <span
                        style={{
                          fontSize: '10px',
                          padding: '2px 8px',
                          borderRadius: '999px',
                          backgroundColor: isBefore ? '#fee2e2' : '#dcfce7',
                          color: isBefore ? '#b91c1c' : '#15803d',
                          fontWeight: '800',
                          textTransform: 'uppercase',
                          letterSpacing: '0.04em'
                        }}
                      >
                        {tagLabel}
                      </span>
                    )}
                  </div>

                  {meta.isLink ? (
                    renderLinkDestinationPreview(v, isDifferent, isBefore)
                  ) : Array.isArray(v) ? (
                    v.length === 0 ? (
                      <div style={{ color: '#94a3b8', fontStyle: 'italic', fontSize: '13px', padding: '4px 0' }}>
                        (Empty list)
                      </div>
                    ) : (
                      <div style={{ display: 'flex', flexDirection: 'column', gap: '8px', marginTop: '4px' }}>
                        {v.map((item, pIdx) => {
                          const otherArr = Array.isArray(otherVal) ? otherVal : [];
                          const otherItem = otherArr[pIdx];
                          const isItemDiff = isOtherEmpty || !otherItem || JSON.stringify(item) !== JSON.stringify(otherItem);
                          const isCardDiff = isDifferent && isItemDiff;

                          if (typeof item === 'object' && item !== null) {
                            const hasLabel = item.label !== undefined || item.key !== undefined;
                            const hasValue = item.value !== undefined || item.val !== undefined;
                            const hasLabelValue = hasLabel && hasValue;
                            const itemLabel = item.label || item.key;
                            const itemValue = item.value !== undefined ? item.value : item.val;
                            const itemHeading =
                              item.title || item.name || item.question || item.headline || item.heading || (hasLabel ? itemLabel : null) || (item.year ? `Year ${item.year}` : null);
                            const itemDetail = item.description || item.desc || item.answer || item.detail || item.text || item.paragraph || item.bio;
                            const itemImg = item.image || item.photo || item.thumbnail || item.logo || item.media_id || item.mediaId || (Array.isArray(item.images) ? item.images[0] : null);
                            const resolvedItemImg = itemImg ? resolveImage(itemImg) : null;

                            return (
                              <div
                                key={pIdx}
                                style={{
                                  padding: '9px 12px',
                                  borderRadius: '6px',
                                  backgroundColor: isCardDiff ? (isBefore ? '#fee2e2' : '#dcfce7') : '#f8fafc',
                                  border: `1px solid ${isCardDiff ? (isBefore ? '#fca5a5' : '#86efac') : '#e2e8f0'}`
                                }}
                              >
                                <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: '8px', flexWrap: 'wrap' }}>
                                  <div style={{ fontWeight: '700', fontSize: '13px', color: isCardDiff ? (isBefore ? '#991b1b' : '#166534') : '#1e293b' }}>
                                    #{pIdx + 1}. {itemHeading || `Point ${pIdx + 1}`}
                                  </div>
                                  {hasLabelValue && (
                                    <span
                                      style={{
                                        fontWeight: '700',
                                        fontSize: '12px',
                                        color: isCardDiff ? (isBefore ? '#7f1d1d' : '#047857') : '#1d4ed8',
                                        background: isCardDiff ? (isBefore ? '#fecaca' : '#a7f3d0') : '#eff6ff',
                                        padding: '2px 8px',
                                        borderRadius: '4px'
                                      }}
                                    >
                                      {String(itemValue)}
                                    </span>
                                  )}
                                </div>
                                {resolvedItemImg && (
                                  <img
                                    src={resolvedItemImg}
                                    alt="Item thumbnail"
                                    style={{ width: '100%', maxHeight: '130px', objectFit: 'cover', borderRadius: '4px', marginTop: '6px' }}
                                    onError={e => {
                                      e.target.style.display = 'none';
                                    }}
                                  />
                                )}
                                {itemDetail && (
                                  <div style={{ fontSize: '12.5px', color: isCardDiff ? (isBefore ? '#7f1d1d' : '#14532d') : '#475569', lineHeight: '1.4', marginTop: '4px' }}>
                                    {itemDetail}
                                  </div>
                                )}
                                {item.link && (
                                  <div style={{ marginTop: '6px' }}>
                                    {renderLinkDestinationPreview(item.link, isCardDiff, isBefore)}
                                  </div>
                                )}
                              </div>
                            );
                          }

                          return (
                            <div
                              key={pIdx}
                              style={{
                                fontSize: '13px',
                                color: isCardDiff ? (isBefore ? '#7f1d1d' : '#064e3b') : '#0f172a',
                                padding: '3px 8px',
                                borderRadius: '4px',
                                backgroundColor: isCardDiff ? (isBefore ? '#fee2e2' : '#dcfce7') : 'transparent'
                              }}
                            >
                              - {String(item)}
                            </div>
                          );
                        })}
                      </div>
                    )
                  ) : (
                    <div
                      style={{
                        fontSize: k === 'title' ? '15px' : '13.5px',
                        fontWeight: k === 'title' ? '700' : '500',
                        lineHeight: '1.5',
                        color: isDifferent ? (isBefore ? '#7f1d1d' : '#064e3b') : '#0f172a',
                        whiteSpace: 'pre-wrap',
                        wordBreak: 'break-word'
                      }}
                    >
                      {valStr || <span style={{ color: '#94a3b8', fontStyle: 'italic' }}>(Empty)</span>}
                    </div>
                  )}
                </div>
              );
            })}
          </div>

          <details className="raw-json-details">
            <summary>View Raw Data</summary>
            <pre className="raw-json-code">{JSON.stringify(parsed, null, 2)}</pre>
          </details>
        </div>
      );
    }

    // 5. Google Maps Embed Preview
    if (fLower.includes('map_embed') && typeof parsed === 'string' && parsed.trim().length > 0) {
      const cleanedMapUrl = cleanGoogleMapsEmbedUrl(parsed);
      return (
        <div className="preview-map-container" style={{ borderRadius: '8px', overflow: 'hidden', border: '1px solid #cbd5e1' }}>
          <iframe
            title={isBefore ? 'Previous Map' : 'Proposed Map'}
            src={cleanedMapUrl}
            style={{ width: '100%', height: '200px', border: 0, display: 'block' }}
            loading="lazy"
          />
          <div style={{ padding: '8px 12px', background: '#f8fafc', fontSize: '12px', color: '#64748b', wordBreak: 'break-all' }}>
            <strong>Embed URL:</strong> {cleanedMapUrl}
          </div>
        </div>
      );
    }

    // 6. Plain Text / Paragraph / Link
    const isTextDifferent = otherParsed !== null && otherParsed !== undefined && String(parsed).trim() !== String(otherParsed).trim();
    const isSingleLinkField =
      fLower.includes('link') ||
      fLower.includes('url') ||
      fLower.includes('href') ||
      (typeof parsed === 'string' && (parsed.startsWith('http://') || parsed.startsWith('https://') || parsed.startsWith('/')));

    if (isSingleLinkField && typeof parsed === 'string') {
      return (
        <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
            <span
              style={{
                fontSize: '11px',
                fontWeight: '700',
                textTransform: 'uppercase',
                letterSpacing: '0.05em',
                color: isTextDifferent ? (isBefore ? '#991b1b' : '#166534') : '#64748b'
              }}
            >
              {isBefore ? 'Live Published Link Destination' : (isTextDifferent ? 'Proposed Link Destination' : 'Link Destination')}
            </span>
            {isTextDifferent && (
              <span
                style={{
                  fontSize: '11px',
                  padding: '3px 8px',
                  borderRadius: '999px',
                  backgroundColor: isBefore ? '#fee2e2' : '#dcfce7',
                  color: isBefore ? '#b91c1c' : '#15803d',
                  fontWeight: '700',
                  textTransform: 'uppercase',
                  letterSpacing: '0.03em'
                }}
              >
                {isBefore ? 'To Be Replaced' : 'New Content'}
              </span>
            )}
          </div>
          {renderLinkDestinationPreview(parsed, isTextDifferent, isBefore)}
        </div>
      );
    }

    return (
      <div
        className="preview-text-block"
        style={{
          backgroundColor: isTextDifferent ? (isBefore ? '#fff5f5' : '#f0fdf4') : '#f8fafc',
          border: `1.5px solid ${isTextDifferent ? (isBefore ? '#fecaca' : '#bbf7d0') : '#e2e8f0'}`,
          borderLeft: `5px solid ${isTextDifferent ? (isBefore ? '#ef4444' : '#10b981') : '#94a3b8'}`,
          padding: '16px 18px',
          borderRadius: '10px',
          boxShadow: '0 1px 3px rgba(0,0,0,0.03)'
        }}
      >
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: '8px' }}>
          <span
            style={{
              fontSize: '11px',
              fontWeight: '700',
              textTransform: 'uppercase',
              letterSpacing: '0.05em',
              color: isTextDifferent ? (isBefore ? '#991b1b' : '#166534') : '#64748b'
            }}
          >
            {isBefore ? 'Live Published Text' : (isTextDifferent ? 'Proposed Revision' : 'Current Value')}
          </span>
          {isTextDifferent && (
            <span
              style={{
                fontSize: '11px',
                padding: '3px 8px',
                borderRadius: '999px',
                backgroundColor: isBefore ? '#fee2e2' : '#dcfce7',
                color: isBefore ? '#b91c1c' : '#15803d',
                fontWeight: '700',
                textTransform: 'uppercase',
                letterSpacing: '0.03em'
              }}
            >
              {isBefore ? 'To Be Replaced' : 'New Content'}
            </span>
          )}
        </div>
        <p
          className="preview-text-content"
          style={{
            color: isTextDifferent ? (isBefore ? '#7f1d1d' : '#064e3b') : '#1e293b',
            fontWeight: isTextDifferent ? '600' : '500',
            fontSize: '15px',
            lineHeight: '1.6',
            margin: 0,
            whiteSpace: 'pre-wrap',
            wordBreak: 'break-word'
          }}
        >
          {String(parsed)}
        </p>
      </div>
    );
  };

  /**
   * READ-ONLY SUBMISSION DETAILS MODAL (MOUNTED VIA PORTAL TO DOCUMENT.BODY)
   * Displays identical Before vs After comparison as Principal's modal, without Approve / Reject buttons.
   */
  const renderModalPortal = () => {
    if (!showModal || !selectedItem) return null;

    const menu = getMenuCategory(selectedItem.page, selectedItem.section_type, selectedItem.field_name, selectedItem.field_id);
    const MenuIcon = menu.icon;
    const sectionName = getSectionDisplayName(selectedItem.section_type, selectedItem.field_name, selectedItem.field_id);
    const fieldDisplay = formatFieldDisplayName(selectedItem.field_name, selectedItem.field_id);
    const formTabId = getFormTabId(selectedItem.page, selectedItem.section_type, selectedItem.field_id, selectedItem.field_name);
    const statusLower = (selectedItem.status || 'pending').toLowerCase();

    const modalContent = (
      <div className="modal-portal-overlay" onClick={closeModal}>
        <div className="approval-detail-modal" onClick={e => e.stopPropagation()}>
          {/* MODAL HEADER */}
          <div className="detail-modal-header">
            <div className="modal-header-top-row">
              <div className="modal-headline-group">
                <h2 className="modal-headline">Submission Details #{selectedItem.id}</h2>
                {statusLower === 'approved' ? (
                  <span className="status-badge status-approved">
                    <CheckCircle2 size={13} />
                    <span>Approved</span>
                  </span>
                ) : statusLower === 'rejected' ? (
                  <span className="status-badge status-rejected">
                    <XCircle size={13} />
                    <span>Rejected</span>
                  </span>
                ) : (
                  <span className="status-badge status-pending">
                    <Clock size={13} />
                    <span>Pending Review</span>
                  </span>
                )}
              </div>
              <button className="modal-btn-close" onClick={closeModal} title="Close modal" aria-label="Close modal">
                <X size={20} />
              </button>
            </div>

            {/* STRUCTURED METADATA CONTEXT BAR */}
            <div className="modal-context-bar">
              <div className="context-card">
                <span className="context-label">Location (Menu &rsaquo; Section)</span>
                <div className="context-value">
                  <span
                    className="menu-badge-compact"
                    style={{
                      backgroundColor: menu.bg,
                      color: menu.color,
                      border: `1px solid ${menu.border}`
                    }}
                  >
                    <MenuIcon size={12} />
                    <span>{menu.name}</span>
                  </span>
                  <span className="context-arrow">&rsaquo;</span>
                  <strong className="context-section-name">{sectionName}</strong>
                </div>
              </div>

              <div className="context-card">
                <span className="context-label">Action / Operation</span>
                <div className="context-value">
                  {(() => {
                    const modalOp = getOperationInfo(selectedItem.field_id, selectedItem.old_value, selectedItem.new_value, selectedItem.field_name);
                    const ModalOpIcon = modalOp.icon;
                    return (
                      <span
                        className="operation-pill-badge"
                        style={{
                          backgroundColor: modalOp.bg,
                          color: modalOp.color,
                          border: `1px solid ${modalOp.border}`,
                          fontWeight: 700
                        }}
                      >
                        <ModalOpIcon size={12} />
                        <span>{modalOp.actionTitle}</span>
                      </span>
                    );
                  })()}
                </div>
              </div>

              <div className="context-card">
                <span className="context-label">Target Field / Item</span>
                <div className="context-value">
                  <span className="context-field-name">{fieldDisplay}</span>
                  <span className="context-field-code">({selectedItem.field_id})</span>
                </div>
              </div>

              <div className="context-card">
                <span className="context-label">Submitted By</span>
                <div className="context-value">
                  <User size={13} className="context-icon" />
                  <span className="context-submitter-name">{selectedItem.submitted_by_name || 'Website Admin'}</span>
                </div>
              </div>

              <div className="context-card">
                <span className="context-label">Submission Date</span>
                <div className="context-value">
                  <Calendar size={13} className="context-icon" />
                  <span>
                    {new Date(selectedItem.submitted_at || Date.now()).toLocaleDateString('en-GB', {
                      day: 'numeric',
                      month: 'short',
                      year: 'numeric',
                      hour: '2-digit',
                      minute: '2-digit'
                    })}
                  </span>
                </div>
              </div>
            </div>
          </div>

          {/* MODAL BODY */}
          <div className="detail-modal-body">
            {statusLower === 'rejected' && (
              <div className="rejection-feedback-banner">
                <div className="feedback-banner-header">
                  <MessageSquare size={16} />
                  <strong>Rejection Feedback Reason from Principal:</strong>
                </div>
                <p className="feedback-banner-text">
                  {selectedItem.rejected_reason || 'No specific rejection note was provided by the Principal.'}
                </p>
                {selectedItem.approved_by_name && (
                  <span className="feedback-reviewer">Reviewed by: {selectedItem.approved_by_name}</span>
                )}
              </div>
            )}

            {detailLoading ? (
              <div className="approval-loading-container" style={{ border: 'none', padding: '40px 20px' }}>
                <RefreshCw size={24} className="spinning text-primary" />
                <p>Loading submission comparison details...</p>
              </div>
            ) : (
              <div className="comparison-container">
                <div className="comparison-column before-column">
                  <div className="comparison-column-header">
                    <div className="column-title-wrap">
                      <CheckCircle2 size={15} className="col-header-icon text-muted" />
                      <span className="column-label before-label">Current Published Version</span>
                    </div>
                    <span className="column-desc">Currently live and visible to the public</span>
                  </div>
                  <div className="comparison-content-box">
                    {renderValuePreview(selectedItem.old_value, selectedItem.field_id, true, selectedItem.new_value)}
                  </div>
                </div>

                <div className="comparison-column after-column">
                  <div className="comparison-column-header">
                    <div className="column-title-wrap">
                      <Sparkles size={15} className="col-header-icon text-primary" />
                      <span className="column-label after-label">
                        {statusLower === 'approved' ? 'Approved Version' : 'Proposed New Changes'}
                      </span>
                    </div>
                    <span className="column-desc">Submitted by Website Admin for Principal approval</span>
                  </div>
                  <div className="comparison-content-box">
                    {renderValuePreview(selectedItem.new_value, selectedItem.field_id, false, selectedItem.old_value)}
                  </div>
                </div>
              </div>
            )}
          </div>

          {/* READ-ONLY MODAL FOOTER (NO APPROVE / REJECT AUTHORITY) */}
          <div className="detail-modal-footer">
            <button type="button" className="btn-modal-cancel" onClick={closeModal}>
              Close
            </button>

            <div className="wa-modal-footer-right">
              <div className={`wa-readonly-footer-badge ${statusLower}`}>
                <Lock size={13} />
                <span>
                  {statusLower === 'approved'
                    ? 'Status: APPROVED & LIVE'
                    : statusLower === 'rejected'
                      ? 'Status: REJECTED BY PRINCIPAL'
                      : 'Status: AWAITING PRINCIPAL APPROVAL (READ-ONLY)'}
                </span>
              </div>

              {formTabId && onNavigateTab && viewerRole !== 'super_admin' && (
                <button
                  type="button"
                  className="wa-btn-open-editor"
                  onClick={() => {
                    closeModal();
                    onNavigateTab(formTabId);
                  }}
                >
                  <ExternalLink size={14} />
                  <span>Open Section Editor</span>
                </button>
              )}
            </div>
          </div>
        </div>

        {/* FULLSCREEN LIGHTBOX OVERLAY */}
        {lightboxImage && (
          <div
            style={{
              position: 'fixed',
              inset: 0,
              backgroundColor: 'rgba(0, 0, 0, 0.88)',
              backdropFilter: 'blur(8px)',
              zIndex: 1000001,
              display: 'flex',
              flexDirection: 'column',
              alignItems: 'center',
              justifyContent: 'center',
              padding: '24px'
            }}
            onClick={() => setLightboxImage(null)}
          >
            <div
              style={{
                position: 'relative',
                maxWidth: '92vw',
                maxHeight: '90vh',
                display: 'flex',
                flexDirection: 'column',
                alignItems: 'center',
                backgroundColor: '#0f172a',
                borderRadius: '12px',
                overflow: 'hidden',
                boxShadow: '0 25px 50px -12px rgba(0, 0, 0, 0.75)'
              }}
              onClick={e => e.stopPropagation()}
            >
              <div
                style={{
                  width: '100%',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'space-between',
                  padding: '12px 18px',
                  backgroundColor: '#1e293b',
                  color: '#f8fafc',
                  boxSizing: 'border-box'
                }}
              >
                <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                  <ImageIcon size={16} style={{ color: '#38bdf8' }} />
                  <strong style={{ fontSize: '13px' }}>{lightboxImage.title || 'Full Resolution Photo Preview'}</strong>
                </div>
                <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
                  <a
                    href={lightboxImage.src}
                    target="_blank"
                    rel="noopener noreferrer"
                    style={{
                      display: 'inline-flex',
                      alignItems: 'center',
                      gap: '4px',
                      color: '#38bdf8',
                      fontSize: '12px',
                      fontWeight: '600',
                      textDecoration: 'none'
                    }}
                  >
                    <ExternalLink size={13} />
                    <span>Open in New Tab</span>
                  </a>
                  <button
                    type="button"
                    onClick={() => setLightboxImage(null)}
                    style={{
                      background: 'transparent',
                      border: 'none',
                      color: '#94a3b8',
                      cursor: 'pointer',
                      padding: '4px',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center'
                    }}
                  >
                    <X size={20} />
                  </button>
                </div>
              </div>
              <div
                style={{
                  padding: '16px',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  maxHeight: 'calc(90vh - 60px)',
                  overflow: 'auto',
                  backgroundColor: '#020617'
                }}
              >
                <img
                  src={lightboxImage.src}
                  alt="Full resolution preview"
                  style={{
                    maxWidth: '100%',
                    maxHeight: 'calc(85vh - 70px)',
                    objectFit: 'contain',
                    borderRadius: '6px'
                  }}
                />
              </div>
            </div>
          </div>
        )}
      </div>
    );

    return createPortal(modalContent, document.body);
  };

  return (
    <div className="approval-page-container">
      <div className="submissions-page-wrapper">
        {/* SUBMISSIONS HEADER */}
        <header className="page-main-header">
          <div>
            <div className="header-breadcrumbs">
              {viewerRole === 'super_admin'
                ? 'Super Admin Portal > Submissions Monitor'
                : 'Website Admin Portal > Submissions'}
            </div>
            <h1 className="header-title">Content Submissions Queue &amp; Approval History</h1>
            <p className="header-subtitle">
              {viewerRole === 'super_admin'
                ? 'Monitor all content modification submissions from Website Admin, inspect before-and-after comparisons, and review approval or rejection status from the Principal.'
                : 'Monitor your submitted content modifications, inspect before-and-after comparisons, and check approval status or feedback notes from the Principal.'}
            </p>
          </div>
          <div className="header-actions">
            <span className="wa-readonly-notice-pill" title="Only Principal has authority to approve or reject submissions">
              <Lock size={13} />
              <span>View Only (Approval by Principal)</span>
            </span>
            <button
              className="refresh-button"
              onClick={() => {
                loadSubmissions('all');
                loadTestimonialsMap();
              }}
              disabled={loading}
              title="Reload list"
            >
              <RefreshCw size={15} className={loading ? 'spinning' : ''} />
              <span>Refresh</span>
            </button>
          </div>
        </header>

        {/* STATUS TABS (PENDING, APPROVED, REJECTED, ALL) */}
        <div className="status-tabs-container">
          <button
            className={`status-tab-btn ${selectedStatusFilter === 'pending' ? 'active' : ''}`}
            onClick={() => {
              setSelectedStatusFilter('pending');
              loadSubmissions('all');
            }}
          >
            <Clock size={15} />
            <span>Pending Review</span>
            <span className="status-count-badge badge-pending">{statusCounts.pending || 0}</span>
          </button>

          <button
            className={`status-tab-btn ${selectedStatusFilter === 'approved' ? 'active' : ''}`}
            onClick={() => {
              setSelectedStatusFilter('approved');
              loadSubmissions('all');
            }}
          >
            <CheckCircle2 size={15} />
            <span>Approved</span>
            <span className="status-count-badge badge-approved">{statusCounts.approved || 0}</span>
          </button>

          <button
            className={`status-tab-btn ${selectedStatusFilter === 'rejected' ? 'active' : ''}`}
            onClick={() => {
              setSelectedStatusFilter('rejected');
              loadSubmissions('all');
            }}
          >
            <XCircle size={15} />
            <span>Rejected</span>
            <span className="status-count-badge badge-rejected">{statusCounts.rejected || 0}</span>
          </button>

          <button
            className={`status-tab-btn ${selectedStatusFilter === 'all' ? 'active' : ''}`}
            onClick={() => {
              setSelectedStatusFilter('all');
              loadSubmissions('all');
            }}
          >
            <ClipboardList size={15} />
            <span>All Submissions</span>
            <span className="status-count-badge badge-all">
              {(statusCounts.pending || 0) + (statusCounts.approved || 0) + (statusCounts.rejected || 0)}
            </span>
          </button>
        </div>

        {/* TOOLBAR FILTER & SEARCH */}
        <div className="toolbar-container">
          <div className="menu-filter-tabs">
            {[
              { key: 'all', label: 'All Menus', icon: ClipboardList },
              { key: 'homepage', label: 'Homepage', icon: Home },
              { key: 'about-us', label: 'About Us', icon: Landmark },
              { key: 'education-program', label: 'Education', icon: GraduationCap },
              { key: 'admissions', label: 'Admissions', icon: FileEdit },
              { key: 'student-life', label: 'Student Life', icon: Activity },
              { key: 'facilities', label: 'Facilities', icon: Building2 },
              { key: 'news-updates', label: 'News & Updates', icon: Newspaper },
              { key: 'parents', label: 'Parents', icon: Users },
              { key: 'careers', label: 'Careers', icon: Briefcase },
              { key: 'contact-us', label: 'Contact Us', icon: Phone },
              { key: 'footer', label: 'Footer', icon: PanelBottom }
            ].map(tab => {
              const TabIcon = tab.icon;
              return (
                <button
                  key={tab.key}
                  className={`menu-filter-pill ${selectedMenuFilter === tab.key ? 'active' : ''}`}
                  onClick={() => setSelectedMenuFilter(tab.key)}
                >
                  <TabIcon size={14} />
                  <span>{tab.label}</span>
                </button>
              );
            })}
          </div>

          <div className="search-bar-wrap">
            <Search size={15} className="search-icon" />
            <input
              type="text"
              className="search-input"
              placeholder="Search ID, field, section, submitter..."
              value={searchQuery}
              onChange={e => setSearchQuery(e.target.value)}
            />
            {searchQuery && (
              <button className="search-clear-btn" onClick={() => setSearchQuery('')} title="Clear search">
                <X size={13} />
              </button>
            )}
          </div>
        </div>

        {/* ERROR STATE */}
        {error && (
          <div className="approval-error-banner">
            <AlertTriangle size={18} className="error-icon" />
            <span>Error loading submissions: {error}</span>
            <button className="btn-retry" onClick={() => loadSubmissions('all')}>
              Retry
            </button>
          </div>
        )}

        {/* LOADING STATE */}
        {loading ? (
          <div className="approval-loading-container">
            <RefreshCw size={26} className="spinning text-primary" />
            <p>Loading submissions...</p>
          </div>
        ) : filteredSubmissions.length === 0 ? (
          /* EMPTY STATE */
          <div className="approval-empty-container">
            <CheckCircle2 size={44} color="#10b981" />
            <h3>No Submissions Found</h3>
            <p>
              {searchQuery || selectedMenuFilter !== 'all'
                ? 'No submissions match your active filter or search keywords.'
                : selectedStatusFilter === 'pending'
                  ? 'There are currently no pending submissions awaiting Principal review.'
                  : `No ${selectedStatusFilter} submissions available.`}
            </p>
            {(searchQuery || selectedMenuFilter !== 'all') && (
              <button
                className="btn-reset-filter"
                onClick={() => {
                  setSelectedMenuFilter('all');
                  setSearchQuery('');
                }}
              >
                Reset Filters
              </button>
            )}
          </div>
        ) : (
          /* PURE TABLE VIEW (IDENTICAL TO PRINCIPAL SUBMISSIONS TABLE, READ-ONLY ACTION) */
          <div className="submissions-table-card">
            <div className="table-responsive">
              <table className="submissions-main-table">
                <colgroup>
                  <col style={{ width: '70px' }} />
                  <col style={{ width: '130px' }} />
                  <col style={{ width: '155px' }} />
                  <col style={{ width: '135px' }} />
                  <col style={{ width: '215px' }} />
                  <col style={{ width: '165px' }} />
                  <col style={{ width: '135px' }} />
                  <col style={{ width: '105px' }} />
                </colgroup>
                <thead>
                  <tr>
                    <th>ID</th>
                    <th>Menu</th>
                    <th>Section</th>
                    <th>Action</th>
                    <th>Target Field</th>
                    <th>Submitted By</th>
                    <th>Status</th>
                    <th style={{ textAlign: 'center' }}>Details</th>
                  </tr>
                </thead>
                <tbody>
                  {filteredSubmissions.map(item => {
                    const menu = getMenuCategory(item.page, item.section_type, item.field_name, item.field_id);
                    const MenuIcon = menu.icon;
                    const sectionName = getSectionDisplayName(item.section_type, item.field_name, item.field_id);
                    const fieldDisplay = formatFieldDisplayName(item.field_name, item.field_id);
                    const op = getOperationInfo(item.field_id, item.old_value, item.new_value, item.field_name);
                    const OpIcon = op.icon;
                    const formattedDate = new Date(item.submitted_at || Date.now()).toLocaleDateString('en-GB', {
                      day: 'numeric',
                      month: 'short',
                      year: 'numeric',
                      hour: '2-digit',
                      minute: '2-digit'
                    });

                    return (
                      <tr key={item.id} className="submission-table-row">
                        <td className="id-cell">
                          <span className="id-badge">#{item.id}</span>
                        </td>
                        <td className="menu-cell">
                          <span
                            className="menu-badge"
                            style={{
                              backgroundColor: menu.bg,
                              color: menu.color,
                              border: `1px solid ${menu.border}`
                            }}
                          >
                            <MenuIcon size={13} />
                            <span>{menu.name}</span>
                          </span>
                        </td>
                        <td className="section-cell">
                          <div className="section-cell-text" title={sectionName}>
                            {sectionName}
                          </div>
                        </td>
                        <td className="action-type-cell">
                          <span
                            className="operation-pill-badge"
                            style={{
                              backgroundColor: op.bg,
                              color: op.color,
                              border: `1px solid ${op.border}`
                            }}
                          >
                            <OpIcon size={12} />
                            <span>{op.iconText}</span>
                          </span>
                        </td>
                        <td className="field-cell">
                          <div className="field-cell-content">
                            <span className="field-name-text" title={fieldDisplay}>
                              {fieldDisplay}
                            </span>
                            <span className="field-code-sub" title={item.field_id}>
                              {item.field_id}
                            </span>
                          </div>
                        </td>
                        <td className="submitter-cell">
                          <div className="submitter-cell-content">
                            <div className="submitter-name" title={item.submitted_by_name || 'Website Admin'}>
                              {item.submitted_by_name || 'Website Admin'}
                            </div>
                            <div className="submitter-date">
                              <Calendar size={11} style={{ display: 'inline', marginRight: '4px', verticalAlign: '-1px' }} />
                              <span>{formattedDate}</span>
                            </div>
                          </div>
                        </td>
                        <td className="status-cell">
                          {item.status === 'approved' ? (
                            <span className="status-badge status-approved">
                              <CheckCircle2 size={12} />
                              <span>Approved</span>
                            </span>
                          ) : item.status === 'rejected' ? (
                            <span className="status-badge status-rejected" title={item.rejected_reason || 'Rejected by Principal'}>
                              <XCircle size={12} />
                              <span>Rejected</span>
                            </span>
                          ) : (
                            <span className="status-badge status-pending">
                              <Clock size={12} />
                              <span>Pending Review</span>
                            </span>
                          )}
                        </td>
                        <td className="action-cell" style={{ textAlign: 'center' }}>
                          <button
                            type="button"
                            className={`btn-action-review ${item.status !== 'pending' ? 'btn-action-view' : ''}`}
                            onClick={() => openDetailsModal(item)}
                            title="View submission comparison details"
                          >
                            <Eye size={14} />
                            <span>Details</span>
                          </button>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>

            <div className="table-footer-info">
              <span>Showing {filteredSubmissions.length} submissions</span>
            </div>
          </div>
        )}
      </div>

      {/* PORTAL MODAL (MOUNTED TO BODY, ZERO CLIPPING) */}
      {renderModalPortal()}
    </div>
  );
}

export default ApprovalStatusHistory;
